// ==== GROUP TRACKING (list/detail/map/New-Edit Group modal) ====
// Split from the original single tempdashboard.js (originally lines 6515-8771).
// Loaded as a plain classic <script defer> alongside the other tempdashboard-*.js files --
// they all share one global scope (like separate parts of one big file), so functions and
// top-level let/const declared in any of these files are visible to all the others, as long
// as the <script> tags stay in the same order every one of these files was split in.

  // ---- GROUP TRACKING: group a set of trackers under a name + time window
  // to see all their paths together on one map (e.g. every bird from one
  // training toss). Two inner sub-views (list / detail) toggled by JS
  // inside one panel, same pattern New Track uses for its own empty/content
  // states. Detail view does a one-time fetch per tracker (not live --
  // this is a "look back at a completed/in-progress flight together" view,
  // not a live-updating one), each drawn in its own color from the same
  // palette used for pigeon map markers.
  let trackerGroups = {}; // groupId -> data, groups this org OWNS
  let joinedFlights = {}; // groupId -> data, groups this org has JOINED (not owned)
  let grpMap = null;
  let grpMapReadyPromise = null;
  let grpMyLocationMarker = null; // viewer's own browser-geolocation marker on the group map, see GrpLocateMeControl

  // Group map layers-control toggles (matches the competitor's own panel) --
  // all default off, same as the competitor's own defaults.
  let grpShowDistanceRings = false;
  let grpShowDirectLineHome = false;
  let grpShowLoftTrajectory = false;
  let grpShowLoftNames = false;
  let grpShowTrackerNames = false;
  let grpCurrentGroupData = null; // last-opened group's data, so a toggle change can redraw without re-fetching from Firestore
  let grpLoftMarkers = []; // rebuilt by grpRenderLoftMarkers -- separate from grpTrackerLayers so toggling loft names doesn't touch tracker data
  let grpExtraLayersGroup = null; // holds distance rings / direct-line-home / loft-trajectory, cleared and redrawn as a whole on any toggle change

  function listenForTrackerGroups() {
    // Admin sees every org's groups (system-wide, same posture as the
    // Trackers tab's unscoped registry listener) -- everyone else stays
    // scoped to their own org only.
    const query = isAdmin ? db.collection('trackerGroups') : db.collection('trackerGroups').where('orgId', '==', currentOrg.slug);
    query.onSnapshot(snapshot => {
      trackerGroups = {};
      snapshot.forEach(doc => { trackerGroups[doc.id] = doc.data(); });
      updateGroupNavCount();
      populateGroupOwnerFilter();
      if (document.getElementById('db-panel-group-tracking').classList.contains('active')) applyGroupSearch();
      if (document.getElementById('db-panel-home').classList.contains('active')) renderHomeTab();
    });
  }

  // Flights this org has JOINED via someone else's invite link -- a
  // participants.<slug>.name range query ("> ''" matches any non-empty
  // name) finds every group this org has a participant entry on, which
  // covers both flights we organised ourselves AND ones we joined; the
  // orgId check below keeps only the latter, since the grid above already
  // covers the former. Admin doesn't get a special case here -- this is
  // "flights MY account joined," same for everyone including admin's own.
  // Prefers the organiser's OWN participant entry (keyed by g.orgId, which
  // is never ambiguous about who owns the group) over the plain
  // organiserName field -- that field can only ever be written by whoever
  // clicks Save, so a group edited by a non-owner (before the guard in the
  // save handler existed) could have it wrong, while each participant's own
  // sub-entry only ever gets overwritten by that same participant.
  function getGroupOrganiserName(g) {
    const ownEntry = g.participants && g.participants[g.orgId];
    return (ownEntry && ownEntry.name) || g.organiserName || 'the flight organiser';
  }

  // Three states, checked in this order: an end time always wins (Ended),
  // then a start time still in the future (Upcoming), otherwise it's live
  // right now (In progress). Shared by every card that shows this badge so
  // "upcoming" doesn't have to be reimplemented per card.
  // Labels aligned with the Create/Edit Group modal's Status dropdown
  // (Planned/In Progress/Finished) -- same computation and CSS classes as
  // before (grp-status-badge--ended/--upcoming), just the displayed word.
  function groupStatusBadgeHtml(g) {
    if (g.endTime) return '<span class="grp-status-badge grp-status-badge--ended">Finished</span>';
    const startMs = g.startTime && g.startTime.toMillis ? g.startTime.toMillis() : null;
    if (startMs && startMs > Date.now()) return '<span class="grp-status-badge grp-status-badge--upcoming">Planned</span>';
    return '<span class="grp-status-badge">In Progress</span>';
  }

  function listenForJoinedFlights() {
    db.collection('trackerGroups').where(`participants.${currentOrg.slug}.name`, '>', '').onSnapshot(snapshot => {
      joinedFlights = {};
      snapshot.forEach(doc => {
        if (doc.data().orgId === currentOrg.slug) return;
        joinedFlights[doc.id] = doc.data();
      });
      updateGroupNavCount();
      if (document.getElementById('db-panel-group-tracking').classList.contains('active')) {
        renderJoinedFlightsList();
        // The "no groups yet" empty state (in applyGroupSearch) also
        // depends on joinedFlights -- re-run it too so it hides/shows
        // correctly even if this listener's first snapshot lands after
        // the owned-groups one already rendered.
        applyGroupSearch();
      }
      if (document.getElementById('db-panel-home').classList.contains('active')) renderHomeTab();
    });
  }

  // Shared by both the My Groups cards and the joined-flight cards so the
  // two sections show the same information in the same format.
  function grpCardDateStr(g) {
    if (!g.startTime || !g.startTime.toMillis) return '—';
    const fmtTimeOnly = (ms) => new Date(ms).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
    const startMs = g.startTime.toMillis();
    const start = fmtDateOnly(startMs) + ', ' + fmtTimeOnly(startMs);
    // Finished: show the full start -- end range, never just the start.
    if (g.endTime && g.endTime.toMillis) {
      const endMs = g.endTime.toMillis();
      return start + ' — ' + fmtDateOnly(endMs) + ', ' + fmtTimeOnly(endMs);
    }
    return start;
  }
  function grpCardTimeMetaStr(g) {
    const startMs = g.startTime && g.startTime.toMillis ? g.startTime.toMillis() : 0;
    if (!g.endTime && startMs > Date.now()) return 'Starts in ' + grpTimeUntilLabel(startMs);
    return g.createdAt && g.createdAt.toMillis ? grpTimeAgoLabel(g.createdAt.toMillis()) + ' ago' : '—';
  }
  // Unique lofts actually taking part = participants with at least one
  // tracker entered (a member who joined but has no tracker isn't counted).
  // Older groups saved before the participants map existed fall back to
  // the organiser's own single loft.
  function grpLoftCount(g) {
    const withTrackers = Object.values(g.participants || {}).filter(p => p && (p.trackerIds || []).length > 0).length;
    if (withTrackers > 0) return withTrackers;
    return (g.trackerIds || []).length > 0 ? 1 : 0;
  }
  // "Shared with the club · N joined · M withdrawn" -- '' for a group that
  // isn't shared. Joined = members other than the organiser who are still
  // in; withdrawn shown separately, only when there are any.
  function grpSharedInfoHtml(g) {
    if (!g.clubShareEnabled) return '';
    const others = Object.entries(g.participants || {}).filter(([slug]) => slug !== g.orgId);
    const withdrawnCount = others.filter(([, p]) => p && p.withdrawnAt).length;
    const joinedCount = others.length - withdrawnCount;
    return `<i class="fa-solid fa-user-group"></i> Shared with the club · <span class="grp-shared-badge-count">${joinedCount} joined</span>${withdrawnCount > 0 ? ` · <span class="grp-shared-badge-withdrawn">${withdrawnCount} withdrawn</span>` : ''}`;
  }
  function grpCardStatsHtml(g) {
    const trackerCount = (g.trackerIds || []).length;
    const loftCount = grpLoftCount(g);
    return `<div class="grp-card-stats">
          <div class="grp-card-stat"><i class="fa-solid fa-satellite-dish"></i><div><b>${trackerCount}</b><span>Tracker${trackerCount !== 1 ? 's' : ''}</span></div></div>
          <div class="grp-card-stat"><i class="fa-solid fa-house"></i><div><b>${loftCount}</b><span>Loft${loftCount !== 1 ? 's' : ''}</span></div></div>
        </div>`;
  }

  function renderJoinedFlightsList() {
    const sectionEl = document.getElementById('grp-joined-section');
    const gridEl = document.getElementById('grp-joined-grid');
    const groupIds = Object.keys(joinedFlights);
    if (groupIds.length === 0) { sectionEl.style.display = 'none'; gridEl.innerHTML = ''; return; }
    sectionEl.style.display = 'block';
    gridEl.innerHTML = '';
    groupIds.forEach(groupId => {
      const g = joinedFlights[groupId];
      const myEntry = (g.participants && g.participants[currentOrg.slug]) || {};
      const myCount = (myEntry.trackerIds || []).length;
      // Only shown when a real release point name exists -- never invented.
      const releaseName = g.releasePoint && g.releasePoint.placeName;
      const card = document.createElement('div');
      card.className = 'grp-card';
      card.innerHTML = `
        <div class="grp-card-head">
          <span class="grp-card-name"><span class="grp-card-trophy grp-card-trophy--joined"><i class="fa-solid fa-share-nodes"></i></span>${g.name}</span>
          <div class="grp-card-head-right">${groupStatusBadgeHtml(g)}</div>
        </div>
        <div class="grp-card-row"><i class="fa-regular fa-calendar"></i>${grpCardDateStr(g)}</div>
        ${releaseName ? `<div class="grp-card-row"><i class="fa-solid fa-location-dot"></i>${releaseName} (Release Point)</div>` : ''}
        <div class="grp-card-row"><i class="fa-solid fa-user"></i>Organised by ${getGroupOrganiserName(g)}</div>
        <div class="grp-card-row"><i class="fa-solid fa-dove"></i>${myCount} of your bird${myCount !== 1 ? 's' : ''} joined</div>
        <div class="grp-card-row"><i class="fa-solid fa-clock"></i>${grpCardTimeMetaStr(g)}</div>
        ${grpCardStatsHtml(g)}
        <div class="grp-card-actions">
          <button type="button" class="grp-action-btn grp-action-btn--primary grp-joined-view-btn"><i class="fa-solid fa-map-location-dot"></i> View</button>
          <button type="button" class="grp-action-btn grp-joined-mybirds-btn"><i class="fa-solid fa-dove"></i> My Birds</button>
        </div>
      `;
      card.querySelector('.grp-joined-view-btn').onclick = () => openGroupDetail(groupId);
      // Reopens the same Join Flight page this org used to join in the
      // first place -- already-entered birds show checked/disabled there,
      // and it's where more birds get added, so no separate UI needed.
      card.querySelector('.grp-joined-mybirds-btn').onclick = () => {
        activateTab('join-flight');
        loadJoinFlightView(groupId);
      };
      gridEl.appendChild(card);
    });
  }

  function applyGroupSearch() {
    document.getElementById('grp-loft-reminder').style.display = (currentOrg && currentOrg.homeLoft) ? 'none' : 'block';
    const allGroupIds = Object.keys(trackerGroups);
    const term = document.getElementById('grp-search').value.trim().toLowerCase();
    const normalizedTerm = term.replace(/[:\-\s]/g, '');
    const termFiltered = !term ? allGroupIds : allGroupIds.filter(groupId => {
      const g = trackerGroups[groupId];
      if ((g.name || '').toLowerCase().includes(term)) return true;
      // Tracker-id and account matching are admin-only -- regular accounts
      // only ever see their own groups, so both would be a no-op for them
      // (same reasoning as the Trackers tab's account-only admin match).
      if (!isAdmin) return false;
      // Tracker-id match -- lets admin find "which group has this tracker"
      // by mac (normalized, same as Trackers tab search) or its raw id,
      // checking every tracker in the group.
      const trackerMatch = (g.trackerIds || []).some(rawId => {
        const regData = registry[rawId] || {};
        const tagId = (regData.mac || rawId || '').toLowerCase();
        return tagId.includes(term) || (normalizedTerm && tagId.replace(/[:\-\s]/g, '').includes(normalizedTerm));
      });
      if (trackerMatch) return true;
      const owner = orgsBySlug[g.orgId] || null;
      return ((owner && owner.name) || '').toLowerCase().includes(term) ||
        ((owner && owner.ownerEmail) || '').toLowerCase().includes(term);
    });
    // Status: derived the same way groupStatusBadgeHtml() derives its label
    // (endTime -> Finished; future startTime -> Planned; else In Progress)
    // so a group can never show one badge but match a different filter.
    const statusFiltered = grpStatusFilter === 'all' ? termFiltered : termFiltered.filter(groupId => {
      const g = trackerGroups[groupId];
      const startMs = g.startTime && g.startTime.toMillis ? g.startTime.toMillis() : 0;
      const status = g.endTime ? 'finished' : (startMs > Date.now() ? 'planned' : 'in_progress');
      return status === grpStatusFilter;
    });
    // Owner: admin-only, populated from the distinct set of orgs behind
    // the currently loaded groups (see populateGroupOwnerFilter below).
    const ownerFiltered = (!isAdmin || grpOwnerFilter === 'all') ? statusFiltered : statusFiltered.filter(groupId => trackerGroups[groupId].orgId === grpOwnerFilter);
    renderGroupTrackingList(allGroupIds, ownerFiltered);
  }

  // Admin-only -- lets an admin narrow "My Groups" (which for them means
  // every account's groups) down to one account, same purpose as the
  // Trackers tab's admin account filter.
  function populateGroupOwnerFilter() {
    const sel = document.getElementById('grp-owner-filter');
    if (!sel || !isAdmin) return;
    const seen = new Set();
    const options = ['<option value="all">All My Groups</option>'];
    Object.values(trackerGroups).forEach(g => {
      if (!g.orgId || seen.has(g.orgId)) return;
      seen.add(g.orgId);
      const label = (orgsBySlug[g.orgId] && orgsBySlug[g.orgId].name) || g.orgId;
      options.push(`<option value="${g.orgId}">${label}</option>`);
    });
    const prevVal = sel.value;
    sel.innerHTML = options.join('');
    if (seen.has(prevVal)) sel.value = prevVal;
  }

  // "2 hours 32 min ago" / "Starts in 2 days" -- fuller wording than the
  // compact timeAgo() used elsewhere (Trackers' "2h ago"), for the list
  // card's own dedicated meta line. Both read real timestamps (createdAt /
  // startTime), nothing simulated.
  function grpTimeAgoLabel(ms) {
    const diffMin = Math.floor((Date.now() - ms) / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return diffMin + ' min';
    const h = Math.floor(diffMin / 60), m = diffMin % 60;
    if (h < 24) return h + ' hour' + (h !== 1 ? 's' : '') + (m ? ' ' + m + ' min' : '');
    const d = Math.floor(h / 24);
    return d + ' day' + (d !== 1 ? 's' : '');
  }
  function grpTimeUntilLabel(ms) {
    const diffMin = Math.floor((ms - Date.now()) / 60000);
    if (diffMin <= 0) return 'now';
    if (diffMin < 60) return diffMin + ' min';
    const h = Math.floor(diffMin / 60);
    if (h < 24) return h + ' hour' + (h !== 1 ? 's' : '');
    const d = Math.floor(h / 24);
    return d + ' day' + (d !== 1 ? 's' : '');
  }

  function renderGroupTrackingList(allGroupIds, filteredGroupIds) {
    const grid = document.getElementById('grp-grid');
    const emptyEl = document.getElementById('grp-empty');
    const countEl = document.getElementById('grp-count');
    const addBtn = document.getElementById('grp-empty-add-btn');
    const paginationEl = document.getElementById('grp-pagination');
    if (!allGroupIds) allGroupIds = Object.keys(trackerGroups);
    if (!filteredGroupIds) filteredGroupIds = allGroupIds;
    countEl.textContent = allGroupIds.length ? `(${allGroupIds.length})` : '';

    // Same reasoning as the Trackers tab's per-page selector -- regular
    // accounts rarely have enough groups to paginate, admins always see it
    // since they're looking at every account's groups combined.
    const perPageWrapEl = document.getElementById('grp-per-page-wrap');
    if (perPageWrapEl) perPageWrapEl.style.display = (isAdmin || allGroupIds.length > 20) ? '' : 'none';
    const footerEl = document.getElementById('grp-footer');
    const mygroupsCountEl = document.getElementById('grp-mygroups-count');
    if (mygroupsCountEl) mygroupsCountEl.textContent = allGroupIds.length ? `(${allGroupIds.length})` : '';

    if (allGroupIds.length === 0) {
      // An account with no groups of its OWN but at least one joined flight
      // isn't really "empty" -- the big "create your first group" pitch
      // right above evidence that groups already exist (below) read as
      // contradictory, so skip it and let the joined-flights section speak
      // for itself instead.
      const hasJoinedFlights = Object.keys(joinedFlights).length > 0;
      emptyEl.style.display = hasJoinedFlights ? 'none' : 'block';
      document.getElementById('grp-empty-title').textContent = 'No groups yet';
      document.getElementById('grp-empty-text').textContent = "Create your first tracking group to see several birds' paths on one map.";
      addBtn.style.display = '';
      grid.innerHTML = '';
      paginationEl.innerHTML = '';
      if (footerEl) footerEl.style.display = 'none';
      return;
    }
    if (filteredGroupIds.length === 0) {
      emptyEl.style.display = 'block';
      document.getElementById('grp-empty-title').textContent = 'No matches';
      document.getElementById('grp-empty-text').textContent = 'No groups match your search.';
      addBtn.style.display = 'none';
      grid.innerHTML = '';
      paginationEl.innerHTML = '';
      if (footerEl) footerEl.style.display = 'none';
      return;
    }
    emptyEl.style.display = 'none';
    if (footerEl) footerEl.style.display = '';
    // Sort: Latest/Oldest (by createdAt) or Upcoming First (by startTime,
    // nearest future first -- already-started groups sort after every
    // still-planned one, then among themselves by soonest start).
    const sortedGroupIds = filteredGroupIds.sort((a, b) => {
      const ga = trackerGroups[a], gb = trackerGroups[b];
      if (grpSortMode === 'upcoming') {
        const now = Date.now();
        const sa = ga.startTime && ga.startTime.toMillis ? ga.startTime.toMillis() : -Infinity;
        const sb = gb.startTime && gb.startTime.toMillis ? gb.startTime.toMillis() : -Infinity;
        const aFuture = sa > now, bFuture = sb > now;
        if (aFuture !== bFuture) return aFuture ? -1 : 1; // still-upcoming groups always before already-started ones
        return aFuture ? (sa - sb) : (sb - sa); // upcoming: soonest first -- already-started: most recent first
      }
      const ta = ga.createdAt && ga.createdAt.toMillis ? ga.createdAt.toMillis() : 0;
      const tb = gb.createdAt && gb.createdAt.toMillis ? gb.createdAt.toMillis() : 0;
      return grpSortMode === 'oldest' ? ta - tb : tb - ta;
    });

    const totalPages = groupsPerPage === Infinity ? 1 : Math.max(1, Math.ceil(sortedGroupIds.length / groupsPerPage));
    if (currentGroupPage > totalPages) currentGroupPage = totalPages;
    const startIdx = groupsPerPage === Infinity ? 0 : (currentGroupPage - 1) * groupsPerPage;
    const groupIds = groupsPerPage === Infinity ? sortedGroupIds : sortedGroupIds.slice(startIdx, startIdx + groupsPerPage);
    const showingEl = document.getElementById('grp-showing');
    if (showingEl) showingEl.textContent = 'Showing ' + (startIdx + 1) + ' to ' + (startIdx + groupIds.length) + ' of ' + sortedGroupIds.length + ' group' + (sortedGroupIds.length !== 1 ? 's' : '');

    renderGroupPagination(totalPages);
    grid.innerHTML = '';
    groupIds.forEach(groupId => {
      const g = trackerGroups[groupId];
      // A group admin is seeing but this org didn't create itself can't
      // sensibly be "Edited" (it's not this account's group config) -- only
      // the permanent admin Delete makes sense for those, same reasoning as
      // the Trackers tab's cross-org handling.
      const isForeignOrg = isAdmin && g.orgId !== currentOrg.slug;
      const ownerOrgLabel = isForeignOrg
        ? (orgsBySlug[g.orgId] ? orgsBySlug[g.orgId].name : (g.orgId || 'Unknown account'))
        : null;
      const isOngoing = !g.endTime;
      const startMs = g.startTime && g.startTime.toMillis ? g.startTime.toMillis() : 0;
      const isPlanned = isOngoing && startMs > Date.now();
      // Date range / relative time / stats come from the shared helpers
      // (see grpCardDateStr etc.) so joined-flight cards match exactly.
      const dateStr = grpCardDateStr(g);
      const releaseName = g.releasePoint && g.releasePoint.placeName;
      // Club flights label it explicitly, same wording as the joined-flight
      // cards; normal groups keep the plain location.
      const locationStr = releaseName ? (g.clubShareEnabled ? releaseName + ' (Release Point)' : releaseName) : '—';
      const timeMetaStr = grpCardTimeMetaStr(g);
      const card = document.createElement('div');
      card.className = 'grp-card';
      card.innerHTML = `
        <div class="grp-card-head">
          <span class="grp-card-name"><span class="grp-card-trophy"><i class="fa-solid fa-trophy"></i></span>${g.name}</span>
          <div class="grp-card-head-right">
            ${groupStatusBadgeHtml(g)}
            <button type="button" class="ltc-kebab-btn" aria-label="More actions"><i class="fa-solid fa-ellipsis-vertical"></i></button>
          </div>
        </div>
        <div class="ltc-kebab-menu">
          <div class="ltc-kebab-menu-item danger grp-delete-item"><i class="fa-solid fa-trash"></i> Delete${isForeignOrg ? ' (admin)' : ''}</div>
        </div>
        ${ownerOrgLabel ? `<div class="grp-card-owner">${ownerOrgLabel}</div>` : ''}
        ${g.clubShareEnabled ? `<div class="grp-shared-badge">${grpSharedInfoHtml(g)}</div>` : ''}
        <div class="grp-card-row"><i class="fa-regular fa-calendar"></i>${dateStr}</div>
        <div class="grp-card-row"><i class="fa-solid fa-location-dot"></i>${locationStr}</div>
        <div class="grp-card-row"><i class="fa-solid fa-user"></i>Created by ${isForeignOrg ? (ownerOrgLabel || 'them') : 'you'}</div>
        <div class="grp-card-row"><i class="fa-solid fa-clock"></i>${timeMetaStr}</div>
        ${grpCardStatsHtml(g)}
        <div class="grp-card-actions">
          <button type="button" class="grp-action-btn grp-action-btn--primary grp-view-btn"><i class="fa-solid fa-map-location-dot"></i> View</button>
          <button type="button" class="grp-action-btn grp-edit-item"><i class="fa-solid fa-pen"></i> Edit</button>
          <!-- Invite only makes sense for a flight that's still ongoing --
               nobody can join and start sharing birds on one that's already
               over. -->
          ${isOngoing ? '<button type="button" class="grp-action-btn grp-action-btn--primary grp-invite-item"><i class="fa-solid fa-link"></i> Invite Link</button>' : ''}
          ${isOngoing && !isPlanned
            ? '<button type="button" class="grp-action-btn grp-action-btn--warn grp-end-item"><i class="fa-solid fa-flag-checkered"></i> End Group</button>'
            : `<button type="button" class="grp-action-btn grp-action-btn--danger-full grp-delete-item2"><i class="fa-solid fa-trash"></i> Delete</button>`}
        </div>
      `;
      card.querySelector('.grp-view-btn').onclick = () => openGroupDetail(groupId);
      card.querySelector('.grp-edit-item').onclick = () => openGroupEditModal(groupId);
      // Same requirement as the Edit form's "Create club link" button
      // (see updateGrpClubLinkButtonState) -- a group with no saved
      // release point yet can't have a real invite link generated for it,
      // so this shows an explanation instead of the link dialog, with a
      // path straight into Edit to go set one. Only present at all when
      // ongoing -- see the isOngoing check in the template above.
      const inviteItem = card.querySelector('.grp-invite-item');
      if (inviteItem) inviteItem.onclick = async () => {
        if (!g.releasePoint) {
          const goEdit = await showConfirmModal(
            'Set the release point and start time first, then save. The start time is the moment everyone else\'s birds start being shared from — nothing before it is ever read — so it has to be settled before the link goes out. Once fanciers have joined you can push it later, but not earlier.',
            'Release point needed',
            'Go to Edit'
          );
          if (goEdit) openGroupEditModal(groupId);
          return;
        }
        // Creating the link for the first time needs the organiser's own
        // loft too (arrival/ranking is measured against it). An already-
        // shared flight can still show its existing link.
        if (!g.clubShareEnabled && !hasLoftLocation()) {
          if (await showConfirmModal(GRP_LOFT_REQUIRED_MSG, 'Loft location needed', 'Go to Settings')) goToLoftSettings();
          return;
        }
        openGroupInviteModal(groupId, g.name);
      };
      const endBtn = card.querySelector('.grp-end-item');
      if (endBtn) {
        endBtn.onclick = async () => {
          if (!(await showConfirmModal(`End group "${g.name}" now? This sets its end time to the current moment -- the trackers themselves keep reporting as normal.`, 'End group?'))) return;
          try {
            // A flight that's over shouldn't stay joinable -- an old invite
            // link opened afterward would otherwise just show a frozen,
            // empty-looking map with no explanation. Closing sharing here
            // too is what makes loadJoinFlightView's "flight ended" state
            // (see below) actually reachable via the real invite link flow.
            await db.collection('trackerGroups').doc(groupId).update({
              endTime: firebase.firestore.Timestamp.now(),
              clubShareEnabled: false
            });
          } catch (e) {
            alert('Could not end the group: ' + e.message);
          }
        };
      }
      const doDelete = async () => {
        if (!(await showConfirmModal(`Delete group "${g.name}"? This only removes the group -- the trackers and their history stay as-is.`, 'Delete group?'))) return;
        try {
          await db.collection('trackerGroups').doc(groupId).delete();
        } catch (e) {
          alert('Could not delete: ' + e.message);
        }
      };
      // Two possible Delete triggers on this card -- the kebab item (always
      // present) and, for a Planned/Finished group, the same action shown
      // as its own 4th button too (see the template above).
      card.querySelector('.grp-delete-item').onclick = () => { kebabMenu.classList.remove('open'); doDelete(); };
      const deleteBtn2 = card.querySelector('.grp-delete-item2');
      if (deleteBtn2) deleteBtn2.onclick = doDelete;

      const kebabBtn = card.querySelector('.ltc-kebab-btn');
      const kebabMenu = card.querySelector('.ltc-kebab-menu');
      kebabBtn.onclick = (e) => {
        e.stopPropagation();
        const wasOpen = kebabMenu.classList.contains('open');
        document.querySelectorAll('.ltc-kebab-menu.open').forEach(m => m.classList.remove('open'));
        if (!wasOpen) { kebabMenu.classList.add('open'); keepKebabMenuInView(kebabMenu); }
      };
      grid.appendChild(card);
    });
  }

  function renderGroupPagination(totalPages) {
    const el = document.getElementById('grp-pagination');
    if (totalPages <= 1) {
      el.innerHTML = '';
      return;
    }
    el.innerHTML = '';

    const prevBtn = document.createElement('button');
    prevBtn.textContent = 'Prev';
    prevBtn.disabled = currentGroupPage === 1;
    prevBtn.onclick = () => { currentGroupPage--; applyGroupSearch(); };
    el.appendChild(prevBtn);

    for (let i = 1; i <= totalPages; i++) {
      const pageBtn = document.createElement('button');
      pageBtn.textContent = i;
      if (i === currentGroupPage) pageBtn.className = 'active';
      pageBtn.onclick = () => { currentGroupPage = i; applyGroupSearch(); };
      el.appendChild(pageBtn);
    }

    const nextBtn = document.createElement('button');
    nextBtn.textContent = 'Next';
    nextBtn.disabled = currentGroupPage === totalPages;
    nextBtn.onclick = () => { currentGroupPage++; applyGroupSearch(); };
    el.appendChild(nextBtn);
  }

  document.getElementById('grp-search').oninput = () => {
    currentGroupPage = 1; // a new search always starts back on page 1
    applyGroupSearch();
  };

  document.getElementById('grp-status-filter').onchange = (e) => {
    grpStatusFilter = e.target.value;
    currentGroupPage = 1;
    applyGroupSearch();
  };

  // Owner filter is admin-only in practice (regular accounts only ever see
  // their own groups, so it always resolves to the same single option for
  // them) -- hidden outright for non-admins rather than left visible-but-
  // useless, same treatment as the Trackers tab's own account filter.
  document.getElementById('grp-owner-filter').style.display = isAdmin ? '' : 'none';
  document.getElementById('grp-owner-filter').onchange = (e) => {
    grpOwnerFilter = e.target.value;
    currentGroupPage = 1;
    applyGroupSearch();
  };

  document.getElementById('grp-sort-select').onchange = (e) => {
    grpSortMode = e.target.value;
    currentGroupPage = 1;
    applyGroupSearch();
  };

  document.getElementById('grp-per-page').onchange = (e) => {
    groupsPerPage = e.target.value === 'all' ? Infinity : parseInt(e.target.value, 10);
    currentGroupPage = 1;
    applyGroupSearch();
  };

  let editingGroupId = null; // null = creating a new group; set = editing this existing one, reusing the same modal

  // grpTrkAllIds/grpTrkCheckedIds persist across re-renders (e.g. typing in
  // the new search box) -- the checklist only ever renders the currently
  // MATCHING rows, so a plain DOM query for ":checked" at save time would
  // silently lose any selection whose row got filtered out of view. Every
  // read of "which trackers are selected" (save handler, Selected Pigeons
  // count, Select All) goes through grpTrkCheckedIds instead.
  let grpTrkAllIds = [];
  let grpTrkCheckedIds = new Set();

  function populateGroupTrackerChecklist(checkedRawIds, foreignOrgId) {
    // Admin editing another org's group: pull THAT org's trackers from the
    // unscoped registry instead of this admin's own memberships, otherwise
    // trackers the admin didn't personally add could never be checked and
    // would silently drop off the group on save. Union with the group's
    // already-assigned ids too, in case a tracker's legacy orgId field
    // doesn't match (registry.orgId is last-write-wins across orgs).
    grpTrkAllIds = foreignOrgId
      ? Array.from(new Set([
          ...Object.keys(registry).filter(rawId => registry[rawId].orgId === foreignOrgId),
          ...checkedRawIds
        ])).sort()
      : Object.keys(myTrackerMemberships).sort();
    grpTrkCheckedIds = new Set(checkedRawIds);
    const searchEl = document.getElementById('grp-trk-search');
    if (searchEl) searchEl.value = '';
    renderGroupTrackerChecklist('');
  }

  function renderGroupTrackerChecklist(term) {
    const listEl = document.getElementById('grp-add-trackers');
    if (!listEl) return;
    if (grpTrkAllIds.length === 0) {
      listEl.innerHTML = '<div class="grp-trk-empty-row">No trackers yet — add one from the Trackers tab first.</div>';
      updateGrpSelectedTrackersCount();
      return;
    }
    const t = (term || '').trim().toLowerCase();
    const normalizedTerm = t.replace(/[:\-\s]/g, '');
    const filteredIds = !t ? grpTrkAllIds : grpTrkAllIds.filter(rawId => {
      const regData = registry[rawId] || {};
      const name = (regData.nickname || rawId || '').toLowerCase();
      const tagId = (regData.mac || rawId || '').toLowerCase();
      return name.includes(t) || tagId.includes(t) || (normalizedTerm && tagId.replace(/[:\-\s]/g, '').includes(normalizedTerm));
    });
    if (filteredIds.length === 0) {
      listEl.innerHTML = '<div class="grp-trk-empty-row">No trackers match your search.</div>';
      syncGrpSelectAllState();
      return;
    }
    listEl.innerHTML = filteredIds.map(rawId => {
      const regData = registry[rawId] || {};
      const label = regData.nickname || rawId;
      const checked = grpTrkCheckedIds.has(rawId) ? 'checked' : '';
      const assignedPigeon = allPigeonDocs.find(d => d.id === regData.assignedPigeonId);
      const battery = typeof regData.lastVernalBattery === 'number' ? regData.lastVernalBattery : null;
      // 3-tier battery color, matching this modal's own spec (green 50%+,
      // orange 20-49%, red below 20%) -- scoped to .grp-trk-grid in CSS so
      // it doesn't touch the Trackers tab's own 2-tier good/low treatment.
      const batteryClass = battery == null ? '' : (battery >= 50 ? 'trk-battery-good' : battery >= 20 ? 'trk-battery-mid' : 'trk-battery-low');
      const pigeonCellHtml = assignedPigeon
        ? `<div class="grp-trk-pigeon-cell">${pigeonAvatarHtml(assignedPigeon.p)}<b>${assignedPigeon.p.name}</b></div>`
        : `<div class="grp-trk-pigeon-cell grp-trk-pigeon-cell--unassigned"><i class="fa-solid fa-dove"></i> Unassigned</div>`;
      // CSS grid row, not a <table> row -- fixed columns (see
      // tempdashboard.css) so a long real pigeon name truncates with an
      // ellipsis instead of stretching the row past the modal and forcing
      // a horizontal scrollbar (the bug this rebuild fixes).
      return `<div class="grp-trk-grid-row" role="row">
        <div role="cell"><input type="checkbox" id="grp-trk-${rawId}" value="${rawId}" ${checked}></div>
        <div role="cell"><span class="grp-trk-name"><i class="fa-solid fa-satellite-dish"></i>${label}</span></div>
        <div role="cell" class="grp-trk-col-pigeon">${pigeonCellHtml}</div>
        <div role="cell" class="grp-trk-ring">${assignedPigeon && assignedPigeon.p.ringNumber ? assignedPigeon.p.ringNumber : '—'}</div>
        <div role="cell"><span class="grp-trk-battery"><i class="fa-solid fa-battery-full ${batteryClass}"></i>${battery != null ? battery + '%' : '—'}</span></div>
      </div>`;
    }).join('');
    listEl.querySelectorAll('input[type="checkbox"]').forEach(cb => {
      cb.onchange = () => {
        if (cb.checked) grpTrkCheckedIds.add(cb.value); else grpTrkCheckedIds.delete(cb.value);
        updateGrpSelectedTrackersCount();
        syncGrpSelectAllState();
      };
    });
    updateGrpSelectedTrackersCount();
    syncGrpSelectAllState();
  }

  // Counts checked TRACKERS, not assigned pigeons -- an unassigned tracker
  // is still a valid, meaningful selection (it just has no bird linked to
  // it yet), so checking one must visibly register here too, not silently
  // show 0.
  function updateGrpSelectedTrackersCount() {
    const el = document.getElementById('grp-selected-trackers-count');
    if (!el) return;
    el.textContent = grpTrkCheckedIds.size;
  }

  function syncGrpSelectAllState() {
    const cb = document.getElementById('grp-trk-selectall');
    if (!cb) return;
    const visibleCbs = document.querySelectorAll('#grp-add-trackers input[type="checkbox"]');
    cb.checked = visibleCbs.length > 0 && Array.from(visibleCbs).every(c => c.checked);
  }

  const grpTrkSearchEl = document.getElementById('grp-trk-search');
  if (grpTrkSearchEl) grpTrkSearchEl.oninput = (e) => renderGroupTrackerChecklist(e.target.value);
  const grpTrkSelectAllEl = document.getElementById('grp-trk-selectall');
  if (grpTrkSelectAllEl) grpTrkSelectAllEl.onchange = (e) => {
    const checked = e.target.checked;
    document.querySelectorAll('#grp-add-trackers input[type="checkbox"]').forEach(cb => {
      cb.checked = checked;
      if (checked) grpTrkCheckedIds.add(cb.value); else grpTrkCheckedIds.delete(cb.value);
    });
    updateGrpSelectedTrackersCount();
  };
  // Release point picker (New/Edit Group modal) -- optional lat/lng + place
  // name for where the birds were let go, matching the competitor's own
  // search/click/drag/geolocate picker. Currently just a stored, displayed
  // field (shown on the group's map as a home-style marker); not yet wired
  // into "Invite the club" itself, same scope note as that feature.
  let grpReleaseMap = null;
  let grpReleaseMarker = null;
  let grpReleasePoint = null; // {lat, lng} or null
  let grpReleaseMapReadyPromise = null;
  const GRP_RELEASE_MAP_DEFAULT_VIEW = [54.5, -3, 5]; // roughly centered on the UK, zoomed out

  function ensureGrpReleaseMap() {
    if (grpReleaseMapReadyPromise) return grpReleaseMapReadyPromise;
    grpReleaseMapReadyPromise = ensureLeafletLoaded().then(() => {
      grpReleaseMap = L.map('grp-release-map').setView(
        [GRP_RELEASE_MAP_DEFAULT_VIEW[0], GRP_RELEASE_MAP_DEFAULT_VIEW[1]], GRP_RELEASE_MAP_DEFAULT_VIEW[2]
      );
      NT_TILE_LAYERS.detailed().addTo(grpReleaseMap);
      grpReleaseMap.on('click', (e) => setGrpReleasePoint(e.latlng.lat, e.latlng.lng));
    });
    return grpReleaseMapReadyPromise;
  }

  // Just calling invalidateSize() wasn't enough on its own -- the map gets
  // created the very first time the modal opens, at which point the modal
  // has JUST become visible and the browser hasn't necessarily finished a
  // real layout pass yet, so Leaflet can compute its internal tile grid
  // against a stale/zero size and never actually request any tiles even
  // after the container's real size is known. Re-applying setView (to
  // wherever the map should currently be centered) after invalidateSize
  // forces Leaflet to redo that calculation against the now-correct size
  // and actually load tiles, instead of sitting blank.
  function refreshGrpReleaseMapView() {
    if (!grpReleaseMap) return;
    grpReleaseMap.invalidateSize();
    if (grpReleasePoint) {
      grpReleaseMap.setView([grpReleasePoint.lat, grpReleasePoint.lng], grpReleaseMap.getZoom());
    } else {
      grpReleaseMap.setView([GRP_RELEASE_MAP_DEFAULT_VIEW[0], GRP_RELEASE_MAP_DEFAULT_VIEW[1]], GRP_RELEASE_MAP_DEFAULT_VIEW[2]);
    }
  }

  function updateGrpReleaseStatus() {
    document.getElementById('grp-release-status').textContent = grpReleasePoint
      ? grpReleasePoint.lat.toFixed(5) + ', ' + grpReleasePoint.lng.toFixed(5)
      : 'No release point set';
    // Read-only Latitude/Longitude fields in the new modal layout -- purely
    // a display mirror of grpReleasePoint, same single source of truth the
    // status text above already uses.
    const latEl = document.getElementById('grp-release-lat');
    const lngEl = document.getElementById('grp-release-lng');
    if (latEl) latEl.value = grpReleasePoint ? grpReleasePoint.lat.toFixed(5) : '';
    if (lngEl) lngEl.value = grpReleasePoint ? grpReleasePoint.lng.toFixed(5) : '';
  }

  // Tracks whether the CURRENT text in the place-name field is something
  // we filled in (search result or reverse-geocode), vs. something the
  // user actually typed themselves. Without this, the first auto-fill made
  // the field look "non-empty" and every later move of the marker refused
  // to update it, treating our own earlier guess as if the user had typed
  // it. A real 'input' event only ever fires from genuine typing -- never
  // from our own `.value = ...` assignments -- so it's a reliable signal.
  let grpReleaseNameIsAutoFilled = true;
  document.getElementById('grp-release-placename').addEventListener('input', () => {
    grpReleaseNameIsAutoFilled = false;
  });

  // Reverse-geocodes a clicked/dragged/geolocated point to fill the place
  // name field -- search already gets a name for free from its own result,
  // but a raw map click or dragged flag has nothing but coordinates.
  // Reuses resolvePointAddress (cache + rate-limited queue, same as
  // Location History/marker popups) -- passing it a plain {lat,lng} with no
  // .id, so it geocodes exactly the same way but never attempts the
  // Firestore write-back that only makes sense for a real saved detection.
  function maybeAutoFillGrpReleasePlaceName(lat, lng) {
    const nameInput = document.getElementById('grp-release-placename');
    if (!grpReleaseNameIsAutoFilled) return; // the user typed their own name -- never overwrite it
    resolvePointAddress({ lat, lng }, addr => {
      // Guards against a slow response landing after the user typed
      // something themselves, or moved the point again, in the meantime.
      if (!grpReleaseNameIsAutoFilled) return;
      if (!grpReleasePoint || grpReleasePoint.lat !== lat || grpReleasePoint.lng !== lng) return;
      nameInput.value = addr;
    });
  }

  // skipZoom lets a caller place the marker without also forcing the fixed
  // zoom-13 view below -- used by the search handler, which applies its own
  // fitBounds instead (see there for why: a fixed close zoom made sense for
  // a specific pinpoint like a map click or "I'm at the release site", but
  // was wrong for a place/country-scale search result). skipAutoName is set
  // by that same search path too, since it already fills a (better) name
  // from the search result itself -- an extra reverse-geocode call there
  // would just waste a request and could overwrite it with a worse phrasing.
  function setGrpReleasePoint(lat, lng, skipZoom, skipAutoName) {
    grpReleasePoint = { lat, lng };
    if (!grpReleaseMarker) {
      grpReleaseMarker = L.marker([lat, lng], {
        draggable: true,
        icon: L.divIcon({ className: '', html: '<i class="fa-solid fa-flag" style="color:#c0392b;font-size:22px;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));"></i>', iconSize: [22, 22], iconAnchor: [3, 20] })
      }).addTo(grpReleaseMap);
      grpReleaseMarker.on('drag', (e) => {
        const ll = e.target.getLatLng();
        grpReleasePoint = { lat: ll.lat, lng: ll.lng };
        updateGrpReleaseStatus();
      });
      // dragend, not drag -- drag fires continuously while the flag is
      // still moving, which would spam Nominatim well past its fair-use
      // expectations; dragend fires once, when the user actually lets go.
      grpReleaseMarker.on('dragend', (e) => {
        const ll = e.target.getLatLng();
        maybeAutoFillGrpReleasePlaceName(ll.lat, ll.lng);
      });
    } else {
      grpReleaseMarker.setLatLng([lat, lng]);
    }
    if (!skipZoom) grpReleaseMap.setView([lat, lng], 13);
    updateGrpReleaseStatus();
    if (!skipAutoName) maybeAutoFillGrpReleasePlaceName(lat, lng);
  }

  function clearGrpReleasePoint() {
    grpReleasePoint = null;
    if (grpReleaseMarker) { grpReleaseMap.removeLayer(grpReleaseMarker); grpReleaseMarker = null; }
    document.getElementById('grp-release-placename').value = '';
    grpReleaseNameIsAutoFilled = true; // blank field -- safe to auto-fill again next time a point is set
    updateGrpReleaseStatus();
  }

  // Shared by both the magnifying-glass button (first result) and clicking
  // a live-search dropdown suggestion (that specific result) -- moves the
  // flag, fits the view to the result's own extent, and prefills the place
  // name, all in one place instead of duplicating this per entry point.
  function applyGrpReleaseSearchResult(r) {
    setGrpReleasePoint(parseFloat(r.lat), parseFloat(r.lon), true, true);
    // Nominatim returns a boundingbox per result (how big the actual place
    // is) -- fitting to that instead of a fixed zoom is what makes
    // searching "UK" zoom out to show the whole country, while searching a
    // street address still zooms in close, both from the same code path.
    // maxZoom caps it so a tiny/precise result (a single address) doesn't
    // over-zoom past a sensible street-level view.
    if (r.boundingbox && r.boundingbox.length === 4) {
      const [south, north, west, east] = r.boundingbox.map(parseFloat);
      grpReleaseMap.fitBounds([[south, west], [north, east]], { maxZoom: 15, padding: [20, 20] });
    } else {
      grpReleaseMap.setView([parseFloat(r.lat), parseFloat(r.lon)], 13);
    }
    // First two comma-separated parts of the full address (e.g. "Dougherty
    // Avenue, San Jose") -- short enough to fit the place-name field
    // without needing the whole county/postcode/country tail.
    document.getElementById('grp-release-placename').value = r.display_name.split(',').slice(0, 2).join(',').trim();
  }

  async function nominatimSearch(q) {
    return fetch(
      `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(q)}`,
      { headers: { 'Accept-Language': 'en' } }
    ).then(r => r.json());
  }

  // Shows a clickable suggestion list (rather than jumping straight to the
  // first result) -- clicking one of the rendered rows is the only thing
  // that actually moves the map, see applyGrpReleaseSearchResult below.
  function hideGrpReleaseSuggestions() {
    const el = document.getElementById('grp-release-suggestions');
    el.classList.remove('open');
    el.innerHTML = '';
  }
  async function showGrpReleaseSuggestions(q) {
    if (!q) return;
    const searchingEl = document.getElementById('grp-release-searching');
    searchingEl.style.display = 'block';
    let results;
    try {
      results = await nominatimSearch(q);
    } catch (err) {
      searchingEl.style.display = 'none';
      showToast('Search failed: ' + err.message);
      return;
    }
    searchingEl.style.display = 'none';
    // The query can change while this was in flight (relevant for the
    // debounced typing path) -- drop a stale response instead of showing
    // suggestions for something no longer typed.
    if (document.getElementById('grp-release-search').value.trim() !== q) return;
    if (!results.length) { hideGrpReleaseSuggestions(); showToast('No matching place found'); return; }
    const suggestEl = document.getElementById('grp-release-suggestions');
    suggestEl.innerHTML = results.map((r, i) =>
      `<div class="grp-release-suggestion" data-idx="${i}">${r.display_name}</div>`
    ).join('');
    suggestEl.classList.add('open');
    suggestEl.querySelectorAll('.grp-release-suggestion').forEach((el, i) => {
      el.onclick = () => {
        document.getElementById('grp-release-search').value = results[i].display_name;
        hideGrpReleaseSuggestions();
        applyGrpReleaseSearchResult(results[i]);
      };
    });
  }

  // Clicking Search (or pressing Enter) searches immediately. Typing also
  // searches on its own, debounced -- once there are 3+ characters, waits
  // ~400ms after the user stops typing before calling Nominatim, so it
  // doesn't fire on every keystroke. Same behavior as the loft search below.
  let grpReleaseSuggestTimer = null;
  document.getElementById('grp-release-search-btn').onclick = () => {
    showGrpReleaseSuggestions(document.getElementById('grp-release-search').value.trim());
  };
  document.getElementById('grp-release-search').addEventListener('input', (e) => {
    clearTimeout(grpReleaseSuggestTimer);
    const q = e.target.value.trim();
    if (q.length < 3) { hideGrpReleaseSuggestions(); return; }
    grpReleaseSuggestTimer = setTimeout(() => showGrpReleaseSuggestions(q), 400);
  });
  document.getElementById('grp-release-search').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); clearTimeout(grpReleaseSuggestTimer); document.getElementById('grp-release-search-btn').click(); }
    if (e.key === 'Escape') hideGrpReleaseSuggestions();
  });
  // Closing on outside click -- not on the input's own blur, since clicking
  // a suggestion also blurs the input just before its own click handler
  // would fire, which raced and closed the dropdown before the click landed.
  document.addEventListener('click', (e) => {
    const wrap = document.getElementById('grp-release-search');
    if (wrap && !wrap.parentElement.contains(e.target)) hideGrpReleaseSuggestions();
  });

  document.getElementById('grp-release-locate-btn').onclick = () => {
    if (!navigator.geolocation) { showToast('Location isn\'t supported by this browser.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setGrpReleasePoint(pos.coords.latitude, pos.coords.longitude),
      () => showToast('Could not get your location — check location permission for this site.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  document.getElementById('grp-release-clear-btn').onclick = () => clearGrpReleasePoint();

  // HOME LOFT LOCATION (Settings panel) -- same search/click/drag/locate
  // picker as the group release point above (shares its CSS via the
  // generic .grp-release-* classes, and its nominatimSearch), just a
  // separate map instance/state and no place-name auto-fill (a loft has no
  // "name" field to fill). Powers "stops sharing once home" on shared
  // flight maps: see participants.<slug>.homeLoft in the group save
  // handler and joinFlight Cloud Function.
  let loftMap = null;
  let loftMarker = null;
  let loftPoint = null; // {lat, lng} or null
  let loftMapReadyPromise = null;

  function ensureLoftMap() {
    if (loftMapReadyPromise) return loftMapReadyPromise;
    loftMapReadyPromise = ensureLeafletLoaded().then(() => {
      loftMap = L.map('loft-map').setView([GRP_RELEASE_MAP_DEFAULT_VIEW[0], GRP_RELEASE_MAP_DEFAULT_VIEW[1]], GRP_RELEASE_MAP_DEFAULT_VIEW[2]);
      NT_TILE_LAYERS.detailed().addTo(loftMap);
      loftMap.on('click', (e) => setLoftPoint(e.latlng.lat, e.latlng.lng));
    });
    return loftMapReadyPromise;
  }

  function refreshLoftMapView() {
    if (!loftMap) return;
    loftMap.invalidateSize();
    if (loftPoint) {
      loftMap.setView([loftPoint.lat, loftPoint.lng], loftMap.getZoom());
    } else {
      loftMap.setView([GRP_RELEASE_MAP_DEFAULT_VIEW[0], GRP_RELEASE_MAP_DEFAULT_VIEW[1]], GRP_RELEASE_MAP_DEFAULT_VIEW[2]);
    }
  }

  function updateLoftStatus() {
    document.getElementById('loft-status').textContent = loftPoint
      ? loftPoint.lat.toFixed(5) + ', ' + loftPoint.lng.toFixed(5)
      : 'No loft location set';
    // Read-only Latitude/Longitude fields in the Loft tab mirror the point.
    document.getElementById('loft-lat').value = loftPoint ? loftPoint.lat.toFixed(5) : '';
    document.getElementById('loft-lng').value = loftPoint ? loftPoint.lng.toFixed(5) : '';
  }

  // Splits a Nominatim "address" object into the 4 fields shown under the
  // map -- pass null to blank them out (cleared point, or a lookup that
  // returned nothing).
  function updateLoftAddressFields(addr) {
    addr = addr || {};
    document.getElementById('loft-addr-street').value =
      [addr.house_number, addr.road].filter(Boolean).join(' ');
    document.getElementById('loft-addr-city').value =
      addr.city || addr.town || addr.village || addr.county || '';
    document.getElementById('loft-addr-postcode').value = addr.postcode || '';
    document.getElementById('loft-addr-country').value = addr.country || '';
  }

  // Reverse-geocodes a clicked/dragged/geolocated point -- a search result
  // already comes with its own address breakdown (see applyLoftSearchResult),
  // but a raw map click or dragged/geolocated marker has nothing but
  // coordinates.
  async function reverseGeocodeLoft(lat, lng) {
    let result;
    try {
      result = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${lat}&lon=${lng}`,
        { headers: { 'Accept-Language': 'en' } }
      ).then(r => r.json());
    } catch (e) {
      return;
    }
    // Guards against a slow response landing after the point moved again.
    if (!loftPoint || loftPoint.lat !== lat || loftPoint.lng !== lng) return;
    updateLoftAddressFields(result && result.address);
  }

  function setLoftPoint(lat, lng, skipZoom, skipAddressLookup) {
    loftPoint = { lat, lng };
    if (!loftMarker) {
      loftMarker = L.marker([lat, lng], {
        draggable: true,
        icon: L.divIcon({ className: '', html: '<i class="fa-solid fa-house" style="color:#2f6fed;font-size:20px;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));"></i>', iconSize: [20, 20], iconAnchor: [10, 18] })
      }).addTo(loftMap);
      loftMarker.on('drag', (e) => {
        const ll = e.target.getLatLng();
        loftPoint = { lat: ll.lat, lng: ll.lng };
        updateLoftStatus();
      });
      // dragend, not drag -- drag fires continuously while still moving,
      // which would spam Nominatim well past its fair-use expectations.
      loftMarker.on('dragend', (e) => {
        const ll = e.target.getLatLng();
        reverseGeocodeLoft(ll.lat, ll.lng);
      });
    } else {
      loftMarker.setLatLng([lat, lng]);
    }
    if (!skipZoom) loftMap.setView([lat, lng], 13);
    updateLoftStatus();
    if (!skipAddressLookup) reverseGeocodeLoft(lat, lng);
  }

  function clearLoftPoint() {
    loftPoint = null;
    if (loftMarker) { loftMap.removeLayer(loftMarker); loftMarker = null; }
    updateLoftStatus();
    updateLoftAddressFields(null);
  }

  function applyLoftSearchResult(r) {
    setLoftPoint(parseFloat(r.lat), parseFloat(r.lon), true, true);
    updateLoftAddressFields(r.address);
    if (r.boundingbox && r.boundingbox.length === 4) {
      const [south, north, west, east] = r.boundingbox.map(parseFloat);
      loftMap.fitBounds([[south, west], [north, east]], { maxZoom: 15, padding: [20, 20] });
    } else {
      loftMap.setView([parseFloat(r.lat), parseFloat(r.lon)], 13);
    }
  }

  function hideLoftSuggestions() {
    const el = document.getElementById('loft-suggestions');
    el.classList.remove('open');
    el.innerHTML = '';
  }
  async function showLoftSuggestions(q) {
    if (!q) return;
    const searchingEl = document.getElementById('loft-searching');
    searchingEl.style.display = 'block';
    let results;
    try {
      results = await nominatimSearch(q);
    } catch (err) {
      searchingEl.style.display = 'none';
      showToast('Search failed: ' + err.message);
      return;
    }
    searchingEl.style.display = 'none';
    if (document.getElementById('loft-search').value.trim() !== q) return;
    if (!results.length) { hideLoftSuggestions(); showToast('No matching place found'); return; }
    const suggestEl = document.getElementById('loft-suggestions');
    suggestEl.innerHTML = results.map((r, i) =>
      `<div class="grp-release-suggestion" data-idx="${i}">${r.display_name}</div>`
    ).join('');
    suggestEl.classList.add('open');
    suggestEl.querySelectorAll('.grp-release-suggestion').forEach((el, i) => {
      el.onclick = () => {
        document.getElementById('loft-search').value = results[i].display_name;
        hideLoftSuggestions();
        applyLoftSearchResult(results[i]);
      };
    });
  }

  // Same debounced-typing behavior as the group release-point search above.
  let loftSuggestTimer = null;
  document.getElementById('loft-search-btn').onclick = () => {
    showLoftSuggestions(document.getElementById('loft-search').value.trim());
  };
  document.getElementById('loft-search').addEventListener('input', (e) => {
    clearTimeout(loftSuggestTimer);
    const q = e.target.value.trim();
    if (q.length < 3) { hideLoftSuggestions(); return; }
    loftSuggestTimer = setTimeout(() => showLoftSuggestions(q), 400);
  });
  document.getElementById('loft-search').addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); clearTimeout(loftSuggestTimer); document.getElementById('loft-search-btn').click(); }
    if (e.key === 'Escape') hideLoftSuggestions();
  });
  document.addEventListener('click', (e) => {
    const wrap = document.getElementById('loft-search');
    if (wrap && !wrap.parentElement.contains(e.target)) hideLoftSuggestions();
  });
  document.getElementById('loft-locate-btn').onclick = () => {
    if (!navigator.geolocation) { showToast('Location isn\'t supported by this browser.'); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => setLoftPoint(pos.coords.latitude, pos.coords.longitude),
      () => showToast('Could not get your location — check location permission for this site.'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };
  document.getElementById('loft-clear-btn').onclick = () => clearLoftPoint();

  document.getElementById('loft-save-btn').onclick = async () => {
    const statusEl = document.getElementById('loft-save-status');
    statusEl.textContent = 'Saving...';
    statusEl.style.color = '#6b7a87';
    try {
      // The 4 address fields ride along with lat/lng so reopening Settings
      // can show them straight from Firestore instead of firing a fresh
      // reverse-geocode call on every visit.
      const homeLoft = loftPoint ? {
        lat: loftPoint.lat,
        lng: loftPoint.lng,
        street: document.getElementById('loft-addr-street').value,
        city: document.getElementById('loft-addr-city').value,
        postcode: document.getElementById('loft-addr-postcode').value,
        country: document.getElementById('loft-addr-country').value
      } : null;
      const orgQuery = await db.collection('organizations').where('ownerUid', '==', auth.currentUser.uid).limit(1).get();
      if (!orgQuery.empty) {
        await orgQuery.docs[0].ref.update({ homeLoft });
        currentOrg.homeLoft = homeLoft;
      }
      // Every flight this org already organised or joined got its OWN copy
      // of homeLoft stamped onto participants.<slug> at save/join time (see
      // the group save handler and the joinFlight Cloud Function) -- that
      // copy is what the shared map actually reads, and it does NOT
      // update on its own just because the org's own homeLoft changed here.
      // Without this, setting your loft for the first time (or moving it)
      // would silently do nothing for any flight already joined before that
      // point -- the home-arrival cutoff and loft pin would never show for
      // those. Same query listenForJoinedFlights uses to find every group
      // this org has a participant entry on, owned or joined.
      const myGroupsSnap = await db.collection('trackerGroups').where(`participants.${currentOrg.slug}.name`, '>', '').get();
      if (!myGroupsSnap.empty) {
        const batch = db.batch();
        myGroupsSnap.forEach(doc => {
          batch.update(doc.ref, { [`participants.${currentOrg.slug}.homeLoft`]: homeLoft });
        });
        await batch.commit();
      }
      statusEl.textContent = 'Saved!';
      statusEl.style.color = '#2e8b57';
    } catch (e) {
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
    }
  };

  async function openGroupAddModal() {
    editingGroupId = null;
    document.getElementById('grp-add-modal-title').textContent = 'Create New Group';
    document.getElementById('grp-add-save').textContent = 'Create Group';
    document.getElementById('grp-add-name').value = '';
    document.getElementById('grp-add-status').textContent = '';
    document.getElementById('grp-add-enddate').value = '';
    document.getElementById('grp-add-endtime').value = '';
    document.getElementById('grp-add-clubflight').checked = false;
    document.getElementById('grp-add-notes').value = '';
    const now = new Date();
    const localNow = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
    document.getElementById('grp-add-startdate').value = localNow.slice(0, 10);
    document.getElementById('grp-add-starttime').value = localNow.slice(11, 16);
    populateGroupTrackerChecklist([]);
    document.getElementById('grp-release-search').value = '';
    hideGrpReleaseSuggestions();
    // No id yet for a brand-new, not-yet-saved group -- nothing to
    // generate a club join link for until it's actually created (the
    // grp-add-clubflight checkbox above seeds clubShareEnabled instead).
    document.getElementById('grp-club-section').style.display = 'none';
    document.getElementById('grp-clubflight-wrap').style.display = '';
    document.getElementById('grp-add-modal').classList.add('open');
    await ensureGrpReleaseMap();
    clearGrpReleasePoint();
    setTimeout(refreshGrpReleaseMapView, 50);
  }
  document.getElementById('grp-add-btn').onclick = openGroupAddModal;
  document.getElementById('grp-empty-add-btn').onclick = openGroupAddModal;

  setupDismissableBanners(['grp-feature-banner', 'grp-info-banner'], 'grp-banner-restore-btn', 'fpl-grp-banners-dismissed');
  const grpHowItWorksLink = document.getElementById('grp-howitworks-link');
  if (grpHowItWorksLink) grpHowItWorksLink.onclick = () => document.getElementById('grp-howitworks-modal').classList.add('open');
  const grpHowItWorksDone = document.getElementById('grp-howitworks-done');
  if (grpHowItWorksDone) grpHowItWorksDone.onclick = () => document.getElementById('grp-howitworks-modal').classList.remove('open');

  document.getElementById('grp-loft-reminder-link').onclick = async (e) => {
    e.preventDefault();
    await openSettingsPanel('loft');
    document.getElementById('loft-map').scrollIntoView({ behavior: 'smooth', block: 'center' });
  };
  document.getElementById('trk-loft-reminder-link').onclick = async (e) => {
    e.preventDefault();
    await openSettingsPanel('loft');
    document.getElementById('loft-map').scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  async function openGroupEditModal(groupId) {
    const g = trackerGroups[groupId];
    if (!g) return;
    editingGroupId = groupId;
    document.getElementById('grp-add-modal-title').textContent = 'Edit Group';
    document.getElementById('grp-add-save').textContent = 'Save Changes';
    document.getElementById('grp-add-name').value = g.name || '';
    document.getElementById('grp-add-status').textContent = '';
    document.getElementById('grp-add-notes').value = g.notes || '';
    const toLocalInput = (d) => { d = new Date(d.getTime() - d.getTimezoneOffset() * 60000); return d.toISOString().slice(0, 16); };
    const startLocal = g.startTime && g.startTime.toMillis ? toLocalInput(new Date(g.startTime.toMillis())) : toLocalInput(new Date());
    document.getElementById('grp-add-startdate').value = startLocal.slice(0, 10);
    document.getElementById('grp-add-starttime').value = startLocal.slice(11, 16);
    const endLocal = g.endTime && g.endTime.toMillis ? toLocalInput(new Date(g.endTime.toMillis())) : '';
    document.getElementById('grp-add-enddate').value = endLocal ? endLocal.slice(0, 10) : '';
    document.getElementById('grp-add-endtime').value = endLocal ? endLocal.slice(11, 16) : '';
    const isForeignOrg = isAdmin && g.orgId !== currentOrg.slug;
    populateGroupTrackerChecklist(g.trackerIds || [], isForeignOrg ? g.orgId : null);
    document.getElementById('grp-release-search').value = '';
    hideGrpReleaseSuggestions();
    // "Fly this with the club" section -- only makes sense for a group that
    // already exists (this is an edit, not a fresh unsaved "New Group").
    // The grp-add-clubflight checkbox is create-only (it just seeds the
    // initial clubShareEnabled value) -- editing an existing group's
    // sharing state happens through grp-club-section below instead, so
    // the checkbox box is hidden here to avoid two controls for one thing.
    document.getElementById('grp-clubflight-wrap').style.display = 'none';
    document.getElementById('grp-club-section').style.display = 'block';
    updateGrpClubLinkButtonState(g);
    document.getElementById('grp-add-modal').classList.add('open');
    await ensureGrpReleaseMap();
    if (g.releasePoint) {
      // skipAutoName -- restoring a saved point shouldn't kick off a fresh
      // reverse-geocode; the saved name (set explicitly below) is either
      // already correct, or intentionally blank.
      setGrpReleasePoint(g.releasePoint.lat, g.releasePoint.lng, false, true);
      document.getElementById('grp-release-placename').value = g.releasePoint.placeName || '';
      // A real saved name is protected from being auto-overwritten by the
      // next click/drag; a blank one is fair game to auto-fill.
      grpReleaseNameIsAutoFilled = !g.releasePoint.placeName;
    } else {
      clearGrpReleasePoint();
    }
    setTimeout(refreshGrpReleaseMapView, 50);
  }

  // Matches the competitor's own warning ("set the release point and start
  // time first, then save") -- start time is already always required to
  // save a group at all, so the only real gate left is a saved release
  // point. Deliberately checked against the SAVED group data, not the
  // picker's current in-progress state, so setting a release point but not
  // yet clicking Save Changes doesn't enable the link early.
  //
  // Once a club link has actually been created (g.clubShareEnabled), the
  // section switches to the "already shared" view instead -- showing the
  // link itself, how many fanciers have joined, and a Stop sharing button --
  // rather than showing "Create club link" again every time the group is
  // reopened for editing.
  function updateGrpClubLinkButtonState(g) {
    const loftOk = hasLoftLocation();
    document.getElementById('grp-club-link-btn').disabled = !g.releasePoint || !loftOk;
    const hintEl = document.getElementById('grp-club-link-hint');
    hintEl.textContent = !g.releasePoint ? 'Set a release point above, then save, to enable this.' : 'Set your loft location in Settings to enable this.';
    hintEl.style.display = (g.releasePoint && loftOk) ? 'none' : 'block';
    const isActive = !!g.clubShareEnabled;
    document.getElementById('grp-club-create-view').style.display = isActive ? 'none' : 'block';
    document.getElementById('grp-club-active-view').style.display = isActive ? 'block' : 'none';
    if (isActive) renderGrpClubActiveView(g);
  }

  function renderGrpClubActiveView(g) {
    const link = window.location.origin + window.location.pathname + '?joinFlight=' + editingGroupId;
    document.getElementById('grp-club-active-link').value = link;
    document.getElementById('grp-club-active-start').textContent =
      g.startTime && g.startTime.toMillis ? fmtDateShort(g.startTime.toMillis()) : '—';
    const joinedCount = Object.keys(g.participants || {}).filter(slug => slug !== currentOrg.slug).length;
    document.getElementById('grp-club-active-count').textContent =
      joinedCount === 0 ? 'Nobody has joined yet.' : `${joinedCount} other fancier${joinedCount !== 1 ? 's' : ''} joined`;
  }

  document.getElementById('grp-club-active-copy').onclick = async () => {
    const input = document.getElementById('grp-club-active-link');
    input.select();
    try {
      await navigator.clipboard.writeText(input.value);
    } catch (e) {
      document.execCommand('copy'); // fallback for older/non-HTTPS contexts where the Clipboard API is unavailable
    }
    showToast('✅ Link copied');
  };

  document.getElementById('grp-club-stop-btn').onclick = async () => {
    if (!editingGroupId) return;
    const ok = await showConfirmModal(
      'New fanciers won\'t be able to join using the existing link. Anyone already flying stays on the map.',
      'Stop sharing this flight?',
      'Stop sharing'
    );
    if (!ok) return;
    await db.collection('trackerGroups').doc(editingGroupId).update({ clubShareEnabled: false });
    const g = trackerGroups[editingGroupId];
    if (g) g.clubShareEnabled = false;
    updateGrpClubLinkButtonState(g || {});
  };

  // Generates and shows the shareable join link for one group -- opening
  // it lands on the real Join Flight view (loadJoinFlightView), where the
  // other person picks their own trackers to add. Built from the CURRENT
  // page's own path (window.location.pathname), not a hardcoded page slug
  // -- this script only ever runs loaded on the dashboard page itself, but
  // that page's actual Shopify slug (test vs. production) isn't something
  // to guess at; using the real current path means the link is correct
  // wherever this happens to be running.
  function openGroupInviteModal(groupId, groupName) {
    const link = window.location.origin + window.location.pathname + '?joinFlight=' + groupId;
    document.getElementById('grp-invite-intro').textContent =
      `Send this link to the other fanciers in your club. Anyone logged in who opens it can add their own birds to "${groupName}", and they'll appear on the same map.`;
    document.getElementById('grp-invite-link-input').value = link;
    document.getElementById('grp-invite-modal').classList.add('open');
    // Generating the link is what "sharing" means -- flag it so reopening
    // Edit later shows the already-shared view (link + joined count + Stop
    // sharing) instead of "Create club link" again.
    const g = trackerGroups[groupId];
    if (g && !g.clubShareEnabled) {
      g.clubShareEnabled = true;
      db.collection('trackerGroups').doc(groupId).update({ clubShareEnabled: true }).catch(() => {});
      if (editingGroupId === groupId) updateGrpClubLinkButtonState(g);
    }
  }

  document.getElementById('grp-invite-copy-btn').onclick = async () => {
    const input = document.getElementById('grp-invite-link-input');
    input.select();
    try {
      await navigator.clipboard.writeText(input.value);
    } catch (e) {
      document.execCommand('copy'); // fallback for older/non-HTTPS contexts where the Clipboard API is unavailable
    }
    showToast('✅ Link copied');
  };
  document.getElementById('grp-invite-done').onclick = () => document.getElementById('grp-invite-modal').classList.remove('open');

  // Second entry point to the same invite flow, from inside the edit form
  // itself ("Fly this with the club" section) -- editingGroupId is only
  // ever set while this modal is in Edit mode, matching the section's own
  // visibility (see openGroupEditModal/openGroupAddModal above).
  document.getElementById('grp-club-link-btn').onclick = () => {
    if (!editingGroupId) return;
    openGroupInviteModal(editingGroupId, document.getElementById('grp-add-name').value.trim());
  };

  document.getElementById('grp-add-cancel').onclick = () => document.getElementById('grp-add-modal').classList.remove('open');

  document.getElementById('grp-add-save').onclick = async () => {
    const name = document.getElementById('grp-add-name').value.trim();
    // Start Date + Start Time are two visible fields now instead of one
    // datetime-local input, joined back into the exact same "YYYY-MM-
    // DDTHH:mm" string the rest of this handler (and new Date(startVal)
    // below) already expects -- no other downstream logic changed.
    const startDateVal = document.getElementById('grp-add-startdate').value;
    const startTimeVal = document.getElementById('grp-add-starttime').value;
    const startVal = startDateVal && startTimeVal ? startDateVal + 'T' + startTimeVal : '';
    // End Date & Time is genuinely optional now -- leaving it blank means
    // the group stays "in progress" (groupStatusBadgeHtml() already derives
    // that from the absence of an endTime); filling it in marks the group
    // finished, same meaning the old Status dropdown's "Finished" option had.
    const endDateVal = document.getElementById('grp-add-enddate').value;
    const endTimeVal = document.getElementById('grp-add-endtime').value;
    const endVal = endDateVal && endTimeVal ? endDateVal + 'T' + endTimeVal : '';
    // Create-only -- hidden (and therefore stale/irrelevant) while editing,
    // see openGroupEditModal. Seeds clubShareEnabled at creation time only;
    // toggling sharing on an existing group still goes through the
    // untouched grp-club-section further down.
    const clubFlightChecked = !editingGroupId && document.getElementById('grp-add-clubflight').checked;
    const statusEl = document.getElementById('grp-add-status');
    // Reads the persistent Set (see populateGroupTrackerChecklist above),
    // not a DOM query -- a tracker checked while a search was narrowing the
    // list stays selected even if its row isn't currently rendered.
    const trackerIds = Array.from(grpTrkCheckedIds);

    if (!name) { statusEl.textContent = 'Enter a group name.'; statusEl.style.color = '#c0392b'; return; }
    // Release Location is optional by default -- only required when
    // creating a club flight with the share link enabled, since that's
    // what other fanciers' joined birds get positioned relative to.
    if (clubFlightChecked && !grpReleasePoint) { statusEl.textContent = 'Set a release location to create a club flight with a share link.'; statusEl.style.color = '#c0392b'; return; }
    if (clubFlightChecked && !hasLoftLocation()) { statusEl.textContent = 'Set your loft location in Settings before creating a club flight.'; statusEl.style.color = '#c0392b'; return; }
    if (trackerIds.length === 0) { statusEl.textContent = 'Select at least one tracker.'; statusEl.style.color = '#c0392b'; return; }
    if (!startVal) { statusEl.textContent = 'Choose a start date and time.'; statusEl.style.color = '#c0392b'; return; }
    if ((endDateVal || endTimeVal) && !endVal) { statusEl.textContent = 'Choose both an end date and an end time, or leave both blank.'; statusEl.style.color = '#c0392b'; return; }

    statusEl.textContent = 'Saving...';
    statusEl.style.color = '#6b7a87';
    try {
      // True organiser = whoever's org actually owns this group (g.orgId),
      // NOT just "whoever is currently saving" -- admin can edit a group it
      // doesn't own (cross-org tools), and blindly stamping isOrganiser/
      // organiserName in that case would incorrectly overwrite the real
      // organiser's identity with the admin's own. Always true when
      // creating a brand new group, since that always makes this org the
      // owner.
      const existingGroup = editingGroupId ? trackerGroups[editingGroupId] : null;
      const savingOrgIsOwner = !editingGroupId || !existingGroup || existingGroup.orgId === currentOrg.slug;

      // Once a shared flight has started, the organiser can't quietly drop
      // one of their own already-entered birds by unchecking it here --
      // same rule everyone else has to follow via Withdraw on the Join
      // Flight page (see submitWithdrawFromFlight). Only blocks REMOVING a
      // previously-checked tracker; adding more is still fine.
      if (editingGroupId && savingOrgIsOwner && existingGroup) {
        const groupIsSharedFlight = !!(existingGroup.clubShareEnabled || (existingGroup.participants && Object.keys(existingGroup.participants).length > 1));
        const groupHasStarted = !!(existingGroup.startTime && existingGroup.startTime.toMillis && existingGroup.startTime.toMillis() <= Date.now());
        if (groupIsSharedFlight && groupHasStarted) {
          const previouslyOwnIds = (existingGroup.participants && existingGroup.participants[currentOrg.slug] && existingGroup.participants[currentOrg.slug].trackerIds) || [];
          const droppedIds = previouslyOwnIds.filter(id => !trackerIds.includes(id));
          if (droppedIds.length > 0) {
            statusEl.textContent = 'This flight has already started, so an entered bird can\'t be removed here — use "My birds on this flight" and Withdraw it instead.';
            statusEl.style.color = '#c0392b';
            return;
          }
        }
      }

      // The top-level trackerIds field is what the shared map actually
      // fetches positions for (see openGroupDetail) -- it must stay the
      // UNION of every participant's own birds, not just this organiser's
      // freshly-checked list, or saving any edit here would silently wipe
      // every other joined fancier's birds off the map.
      let mergedTrackerIds = trackerIds;
      if (editingGroupId && savingOrgIsOwner && existingGroup) {
        const otherParticipantsTrackerIds = Object.entries(existingGroup.participants || {})
          .filter(([slug]) => slug !== currentOrg.slug)
          .flatMap(([, p]) => p.trackerIds || []);
        mergedTrackerIds = Array.from(new Set([...otherParticipantsTrackerIds, ...trackerIds]));
      }

      const data = {
        name, trackerIds: mergedTrackerIds,
        startTime: firebase.firestore.Timestamp.fromMillis(new Date(startVal).getTime()),
        endTime: endVal ? firebase.firestore.Timestamp.fromMillis(new Date(endVal).getTime()) : null,
        releasePoint: grpReleasePoint ? {
          lat: grpReleasePoint.lat,
          lng: grpReleasePoint.lng,
          placeName: document.getElementById('grp-release-placename').value.trim() || null
        } : null,
        notes: document.getElementById('grp-add-notes').value.trim() || null
      };
      // Kept in sync on every OWNER save (not just at creation) so a group
      // saved before this field existed picks up a real name as soon as
      // its organiser edits it, instead of the Join Flight page falling
      // back to generic "the flight organiser" text forever.
      if (savingOrgIsOwner) data.organiserName = currentOrg.name;
      // "Who's flying" reads this "participants" map (keyed by org slug --
      // name + which tracker ids are theirs) instead of the
      // "organizations" collection, which is locked to each org's own
      // owner and unreadable by a visiting fancier. Kept in sync with the
      // organiser's own edits here too, not just joins via joinFlight
      // (which writes its own participants.<slug> entry the same way).
      // homeLoft riding along on the participant entry (not just the
      // organizations doc) is what lets the shared flight map know where
      // THIS org's birds live, without needing to read the organizations
      // collection (locked to its own owner) -- same workaround as name.
      if (editingGroupId) {
        data[`participants.${currentOrg.slug}`] = { name: currentOrg.name, trackerIds, isOrganiser: savingOrgIsOwner, homeLoft: currentOrg.homeLoft || null };
        await db.collection('trackerGroups').doc(editingGroupId).update(data);
      } else {
        await db.collection('trackerGroups').add({
          ...data,
          orgId: currentOrg.slug,
          clubShareEnabled: clubFlightChecked,
          participants: { [currentOrg.slug]: { name: currentOrg.name, trackerIds, isOrganiser: true, homeLoft: currentOrg.homeLoft || null } },
          createdAt: firebase.firestore.FieldValue.serverTimestamp()
        });
      }
      statusEl.textContent = editingGroupId ? 'Saved!' : 'Created!';
      statusEl.style.color = '#2e8b57';
      setTimeout(() => document.getElementById('grp-add-modal').classList.remove('open'), 500);
    } catch (e) {
      statusEl.textContent = e.message;
      statusEl.style.color = '#c0392b';
    }
  };

  // Same Light/Detailed/Satellite picker as New Track (reuses the same
  // NT_TILE_LAYERS factory functions, which aren't tied to a specific map
  // instance), positioned top-right here instead of bottom-right so it
  // sits up near the zoom control.
  let grpBaseLayer = null;
  let grpMapType = 'detailed';

  function grpSetMapType(type) {
    if (!NT_TILE_LAYERS[type] || !grpMap) return;
    grpMapType = type;
    if (grpBaseLayer) grpMap.removeLayer(grpBaseLayer);
    grpBaseLayer = NT_TILE_LAYERS[type]();
    grpBaseLayer.addTo(grpMap);
    grpBaseLayer.bringToBack();
  }

  // NtMapTypeControl/NtFullscreenControl and these Grp equivalents are
  // defined INSIDE initGroupMap()/initNewTrackMap() (not at top level)
  // because they call L.Control.extend(...) immediately -- if Leaflet
  // hasn't been lazy-loaded yet, L would be undefined and that call would
  // throw, aborting the rest of this script. Defining them only after
  // ensureLeafletLoaded() resolves keeps them safe either way.
  function initGroupMap() {
    if (grpMapReadyPromise) return grpMapReadyPromise;
    grpMapReadyPromise = ensureLeafletLoaded().then(() => {
      const GrpMapTypeControl = L.Control.extend({
        options: { position: 'topright' },
        onAdd: function () {
          const wrap = L.DomUtil.create('div', 'nt-maptype-wrap nt-maptype-wrap--top');
          // Same combined panel as the competitor's own layers control --
          // map type radios plus the extra-layer checkboxes together, not
          // two separate controls.
          wrap.innerHTML = `
            <div class="nt-maptype-panel">
              <label><input type="radio" name="grp-maptype" value="light"> Light</label>
              <label><input type="radio" name="grp-maptype" value="detailed" checked> Detailed</label>
              <label><input type="radio" name="grp-maptype" value="satellite"> Satellite</label>
              <hr style="margin:6px 0;border:none;border-top:1px solid #e5e8eb;">
              <label><input type="checkbox" id="grp-layer-distance-rings"> Distance from loft</label>
              <label><input type="checkbox" id="grp-layer-direct-line"> Direct line home</label>
              <label><input type="checkbox" id="grp-layer-loft-trajectory"> Loft trajectory</label>
              <label><input type="checkbox" id="grp-layer-loft-names"> Display loft names</label>
              <label><input type="checkbox" id="grp-layer-tracker-names"> Display tracker names</label>
            </div>
            <button type="button" class="nt-maptype-toggle" aria-label="Map type"><i class="fa-solid fa-layer-group"></i></button>
          `;
          L.DomEvent.disableClickPropagation(wrap);
          wrap.querySelector('.nt-maptype-toggle').addEventListener('click', () => {
            wrap.classList.toggle('open');
          });
          wrap.querySelectorAll('input[name="grp-maptype"]').forEach(input => {
            input.addEventListener('change', () => {
              grpSetMapType(input.value);
              wrap.classList.remove('open');
            });
          });
          // Checkboxes don't close the panel on change (unlike the map-type
          // radios) -- someone toggling several of these in a row shouldn't
          // have to reopen it each time.
          wrap.querySelector('#grp-layer-distance-rings').addEventListener('change', (e) => {
            grpShowDistanceRings = e.target.checked;
            grpRenderExtraLayers();
          });
          wrap.querySelector('#grp-layer-direct-line').addEventListener('change', (e) => {
            grpShowDirectLineHome = e.target.checked;
            grpRenderExtraLayers();
          });
          wrap.querySelector('#grp-layer-loft-trajectory').addEventListener('change', (e) => {
            grpShowLoftTrajectory = e.target.checked;
            grpRenderExtraLayers();
          });
          wrap.querySelector('#grp-layer-loft-names').addEventListener('change', (e) => {
            grpShowLoftNames = e.target.checked;
            grpRenderLoftMarkers(grpCurrentGroupData);
          });
          wrap.querySelector('#grp-layer-tracker-names').addEventListener('change', (e) => {
            grpShowTrackerNames = e.target.checked;
            grpRedrawAllTrackers();
          });
          return wrap;
        }
      });

      // Same hand-built "locate me" control as New Track's NtLocateMeControl
      // -- the VIEWER's own browser location (Geolocation API), not any
      // tracker's, purely client-side.
      const GrpLocateMeControl = L.Control.extend({
        options: { position: 'topleft' },
        onAdd: function () {
          const btn = L.DomUtil.create('button', 'nt-fullscreen-btn');
          btn.type = 'button';
          btn.setAttribute('aria-label', 'Show my location');
          btn.innerHTML = '<i class="fa-solid fa-location-crosshairs"></i>';
          L.DomEvent.disableClickPropagation(btn);
          btn.addEventListener('click', () => {
            if (!navigator.geolocation) {
              showToast('Location isn\'t supported by this browser.');
              return;
            }
            btn.disabled = true;
            navigator.geolocation.getCurrentPosition(
              (pos) => {
                btn.disabled = false;
                const { latitude, longitude } = pos.coords;
                if (grpMyLocationMarker) grpMap.removeLayer(grpMyLocationMarker);
                grpMyLocationMarker = L.marker([latitude, longitude], {
                  icon: L.divIcon({ className: '', html: grpPulseIconHtml('#2f6fed'), iconSize: [44, 44], iconAnchor: [22, 22] })
                }).addTo(grpMap);
                grpMyLocationMarker.bindPopup('<b>You are here</b>').openPopup();
                grpMap.setView([latitude, longitude], 15, { animate: true });
              },
              () => {
                btn.disabled = false;
                showToast('Could not get your location — check location permission for this site.');
              },
              { enableHighAccuracy: true, timeout: 10000 }
            );
          });
          return btn;
        }
      });

      // Same hand-built fullscreen control as New Track's NtFullscreenControl
      // (fullscreens the whole .nt-map-col -- map + legend + play card -- not
      // just the map tile), targeting #grp-map-col instead.
      const GrpFullscreenControl = L.Control.extend({
        options: { position: 'topleft' },
        onAdd: function () {
          const btn = L.DomUtil.create('button', 'nt-fullscreen-btn');
          btn.type = 'button';
          btn.setAttribute('aria-label', 'View fullscreen');
          btn.innerHTML = '<i class="fa-solid fa-expand"></i>';
          L.DomEvent.disableClickPropagation(btn);
          const wrap = document.getElementById('grp-map-col');
          btn.addEventListener('click', () => {
            if (document.fullscreenElement === wrap) {
              document.exitFullscreen();
            } else if (wrap.requestFullscreen) {
              wrap.requestFullscreen();
            } else if (wrap.webkitRequestFullscreen) {
              wrap.webkitRequestFullscreen();
            }
          });
          document.addEventListener('fullscreenchange', () => {
            const isFull = document.fullscreenElement === wrap;
            btn.innerHTML = isFull ? '<i class="fa-solid fa-compress"></i>' : '<i class="fa-solid fa-expand"></i>';
            setTimeout(() => grpMap && grpMap.invalidateSize(), 50);
          });
          return btn;
        }
      });

      grpMap = L.map('grp-map').setView([51.5074, -0.1278], 6);
      grpSetMapType(grpMapType);
      new GrpMapTypeControl().addTo(grpMap);
      new GrpLocateMeControl().addTo(grpMap);
      new GrpFullscreenControl().addTo(grpMap);
    });
    return grpMapReadyPromise;
  }

  // Multiple live listeners can be open at once (one per tracker in an
  // ongoing group) -- tracked together so they can all be detached at once
  // when leaving the detail view or opening a different group.
  let grpDetailUnsubscribers = [];
  let grpTrackerLayers = {}; // rawId -> { line, markers: [] } -- so one tracker's live update can redraw just its own layers
  let grpDetailReleaseMarker = null; // this group's release-point marker (if any), set in openGroupDetail -- reserved for a "Go to release point" button, not wired up yet
  let grpTrackerData = {}; // rawId -> { points, color, label } -- latest known data per tracker, also feeds playback
  let grpVisibility = {}; // rawId -> boolean, toggled by the table's checkboxes

  function grpDetachDetailListeners() {
    grpDetailUnsubscribers.forEach(unsub => unsub());
    grpDetailUnsubscribers = [];
  }

  // Same pulsing "most recent" marker as New Track's .nt-pulse-icon, but
  // color-parameterized per tracker (New Track's version is hardcoded red,
  // since it only ever shows one tracker at a time) -- colors are inlined
  // directly on each layer instead of relying on the shared CSS class.
  function grpPulseIconHtml(hex) {
    const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
    return `<div class="nt-pulse-icon">
      <div class="nt-pulse-halo" style="background:rgba(${r},${g},${b},0.18);border-color:rgba(${r},${g},${b},0.6);"></div>
      <div class="nt-pulse-ring" style="background:rgba(${r},${g},${b},0.55);"></div>
      <div class="nt-pulse-dot" style="background:${hex};"></div>
    </div>`;
  }

  // Static (non-pulsing, deliberately -- this bird isn't "currently
  // reporting" within this flight anymore) marker for wherever a tracker's
  // shared points got cut off, home-arrival or withdrawal.
  function grpCutoffIconHtml(hex, reason) {
    const icon = reason === 'home' ? 'fa-house' : 'fa-circle-pause';
    return `<div style="width:30px;height:30px;border-radius:50%;background:${hex};border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;">
      <i class="fa-solid ${icon}" style="color:#fff;font-size:13px;"></i>
    </div>`;
  }

  // Each participant's own loft (Settings -> Home Loft Location), if
  // they've set one -- colored to match that participant's OWN track color
  // (their first tracker's color in the shared legend/line palette) instead
  // of one flat color for everyone, so it's obvious at a glance whose loft
  // is whose and which bird's line it lines up with. Pulled out of
  // openGroupDetail into its own function so the "Display loft names"
  // toggle can rebuild just these, without touching tracker data.
  function grpRenderLoftMarkers(g) {
    if (!g || !grpMap) return;
    grpLoftMarkers.forEach(m => grpMap.removeLayer(m));
    grpLoftMarkers = [];
    Object.entries(g.participants || {}).forEach(([slug, p]) => {
      if (!p.homeLoft) return;
      const firstTrackerIdx = (g.trackerIds || []).indexOf((p.trackerIds || [])[0]);
      const loftColor = MARKER_COLOR_PALETTE[firstTrackerIdx >= 0 ? firstTrackerIdx % MARKER_COLOR_PALETTE.length : 0].hex;
      const isMe = currentOrg && slug === currentOrg.slug;
      const loftLabel = isMe ? 'Your loft' : `${p.name || 'Unknown'}'s loft`;
      const marker = L.marker([p.homeLoft.lat, p.homeLoft.lng], {
        icon: L.divIcon({ className: '', html: `<i class="fa-solid fa-house" style="color:${loftColor};font-size:18px;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));"></i>`, iconSize: [18, 18], iconAnchor: [9, 16] })
      }).addTo(grpMap).bindTooltip(loftLabel, { permanent: grpShowLoftNames, direction: 'top', offset: [0, -14] });
      grpLoftMarkers.push(marker);
    });
  }

  // Distance-rings / direct-line-home / loft-trajectory -- the rest of the
  // competitor's layers-control panel. All grouped into one layer group so
  // any toggle change just clears and redraws the whole thing fresh,
  // instead of tracking each piece separately.
  function grpRenderExtraLayers() {
    const g = grpCurrentGroupData;
    if (!g || !grpMap) return;
    if (grpExtraLayersGroup) grpMap.removeLayer(grpExtraLayersGroup);
    grpExtraLayersGroup = L.layerGroup().addTo(grpMap);

    // Distance from loft -- concentric rings around THIS viewer's own loft
    // only (not every participant's -- the competitor's own panel measures
    // from "your" loft), at 25/50/100km, each labeled.
    if (grpShowDistanceRings && currentOrg && currentOrg.homeLoft) {
      const center = [currentOrg.homeLoft.lat, currentOrg.homeLoft.lng];
      [25, 50, 100].forEach(km => {
        L.circle(center, {
          radius: km * 1000, color: '#2c3540', weight: 1.5, dashArray: '4,4', fill: false
        }).addTo(grpExtraLayersGroup);
        // Label sits at the top of the ring (due north), matching the
        // competitor's own placement.
        const labelPoint = L.GeometryUtil && L.GeometryUtil.destination
          ? L.GeometryUtil.destination(L.latLng(center), 0, km * 1000)
          : L.latLng(center[0] + km / 111, center[1]); // ~111km per degree latitude, a fine approximation at this scale
        L.marker(labelPoint, {
          icon: L.divIcon({ className: '', html: `<span style="background:#fff;padding:1px 6px;border-radius:4px;font-size:11px;color:#4a5560;box-shadow:0 1px 3px rgba(0,0,0,0.25);white-space:nowrap;">${km} km</span>`, iconSize: [0, 0] })
        }).addTo(grpExtraLayersGroup);
      });
    }

    // Direct line home -- release point to each participant's own loft,
    // colored to match their bird's track.
    if (grpShowDirectLineHome && g.releasePoint) {
      Object.entries(g.participants || {}).forEach(([slug, p]) => {
        if (!p.homeLoft) return;
        const firstTrackerIdx = (g.trackerIds || []).indexOf((p.trackerIds || [])[0]);
        const lineColor = MARKER_COLOR_PALETTE[firstTrackerIdx >= 0 ? firstTrackerIdx % MARKER_COLOR_PALETTE.length : 0].hex;
        L.polyline([[g.releasePoint.lat, g.releasePoint.lng], [p.homeLoft.lat, p.homeLoft.lng]], {
          color: lineColor, weight: 2, dashArray: '6,6', opacity: 0.8
        }).addTo(grpExtraLayersGroup);
      });
    }

    // Loft trajectory -- NOT release-to-loft (that's "Direct line home"
    // above) -- this connects every participant's loft to every OTHER
    // participant's loft, showing how far apart the fanciers' homes are
    // from each other. Nothing to draw with fewer than 2 lofts.
    if (grpShowLoftTrajectory) {
      const lofts = Object.values(g.participants || {})
        .filter(p => p.homeLoft)
        .map(p => [p.homeLoft.lat, p.homeLoft.lng]);
      for (let i = 0; i < lofts.length; i++) {
        for (let j = i + 1; j < lofts.length; j++) {
          L.polyline([lofts[i], lofts[j]], {
            color: '#c0651b', weight: 2.5, dashArray: '4,6', opacity: 1
          }).addTo(grpExtraLayersGroup);
        }
      }
    }
  }

  function grpComputeStats(points) {
    let totalKm = 0, topSpeedMPerMin = 0;
    for (let i = 1; i < points.length; i++) {
      const km = haversineKm(points[i - 1].lat, points[i - 1].lng, points[i].lat, points[i].lng);
      totalKm += km;
      const minutes = (points[i].t - points[i - 1].t) / 60000;
      if (minutes > 0) topSpeedMPerMin = Math.max(topSpeedMPerMin, (km * 1000) / minutes);
    }
    return { totalKm, topSpeedMPerMin };
  }

  function grpRenderTrackerTable() {
    const body = document.getElementById('grp-tracker-table-body');
    body.innerHTML = '';
    Object.keys(grpTrackerData).forEach(rawId => {
      const { points, color, label } = grpTrackerData[rawId];
      const { totalKm, topSpeedMPerMin } = grpComputeStats(points);
      const lastSeen = points.length > 0 ? timeAgo(points[points.length - 1].t) : '—';
      const row = document.createElement('tr');
      row.innerHTML = `
        <td><input type="checkbox" class="grp-visibility-cb" data-rawid="${rawId}" ${grpVisibility[rawId] !== false ? 'checked' : ''}></td>
        <td><span class="nt-legend-dot" style="background:${color.hex};"></span> ${label}</td>
        <td>${points.length}</td>
        <td>${totalKm > 0 ? totalKm.toFixed(1) + ' km' : '—'}</td>
        <td>${topSpeedMPerMin > 0 ? Math.round(topSpeedMPerMin) + ' m/min' : '—'}</td>
        <td>${lastSeen}</td>
      `;
      row.querySelector('.grp-visibility-cb').onchange = (e) => {
        grpSetTrackerVisibility(rawId, e.target.checked);
      };
      body.appendChild(row);
    });
    grpRenderResults();
  }

  function grpFmtDuration(ms) {
    const s = Math.round(ms / 1000);
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? `${h} h ${String(m).padStart(2, '0')} min` : `${m} min ${String(sec).padStart(2, '0')} s`;
  }

  // Race results for the open flight. A bird "arrives" the moment it comes
  // within 200m of its own loft (same rule that already drops it off the
  // map, see applyGrpShareCutoffs). Ranked by SPEED -- straight-line
  // release-to-loft distance / (arrival - flight start) -- rather than raw
  // flight time, so lofts at different distances from the release point
  // compete fairly. Birds still flying, withdrawn, or with no loft location
  // set are listed after the ranked ones, unranked.
  function grpRenderResults() {
    const card = document.getElementById('grp-results-card');
    if (!card) return;
    const g = grpCurrentGroupData;
    const rp = g && g.releasePoint;
    if (!g || !rp || !g.startTime || !g.startTime.toMillis) { card.style.display = 'none'; return; }
    const startMs = g.startTime.toMillis();
    const rows = Object.keys(grpTrackerData).map(rawId => {
      const owner = findOwningParticipant(g, rawId);
      const loft = owner && owner.homeLoft ? owner.homeLoft : null;
      const distKm = loft ? haversineKm(rp.lat, rp.lng, loft.lat, loft.lng) : null;
      const arrivalMs = grpTrackerArrival[rawId] || null;
      const row = { label: grpTrackerData[rawId].label, loftName: owner ? (owner.name || '—') : '—', distKm, arrivalMs, state: 'flying' };
      if (arrivalMs && distKm != null && arrivalMs > startMs) {
        row.state = 'arrived';
        row.flightMs = arrivalMs - startMs;
        row.speed = (distKm * 1000) / (row.flightMs / 60000);
      } else if (grpTrackerCutoffReason[rawId] === 'withdrawn') row.state = 'withdrawn';
      else if (!loft) row.state = 'noloft';
      return row;
    });
    if (rows.length === 0) { card.style.display = 'none'; return; }
    card.style.display = '';
    const order = { arrived: 0, flying: 1, withdrawn: 2, noloft: 3 };
    rows.sort((a, b) => order[a.state] - order[b.state] || (b.speed || 0) - (a.speed || 0));
    const stateText = { flying: 'In flight', withdrawn: 'Withdrawn', noloft: 'No loft location set' };
    let rank = 0;
    document.getElementById('grp-results-body').innerHTML = rows.map(r => {
      const isArrived = r.state === 'arrived';
      if (isArrived) rank++;
      return `<tr>
        <td>${isArrived ? rank : '—'}</td>
        <td>${r.label}</td>
        <td>${r.loftName}</td>
        <td>${r.distKm != null ? r.distKm.toFixed(1) + ' km' : '—'}</td>
        <td>${isArrived ? grpFmtDuration(r.flightMs) : '<span style="color:#8a97a3;">' + stateText[r.state] + '</span>'}</td>
        <td>${isArrived ? '<b>' + Math.round(r.speed).toLocaleString('en-GB') + ' m/min</b>' : '—'}</td>
      </tr>`;
    }).join('');
  }

  function grpSetTrackerVisibility(rawId, visible) {
    grpVisibility[rawId] = visible;
    const layers = grpTrackerLayers[rawId];
    if (layers) {
      [layers.casing, layers.line, layers.decorator].forEach(l => {
        if (!l) return;
        visible ? l.addTo(grpMap) : grpMap.removeLayer(l);
      });
      layers.markers.forEach(m => visible ? m.addTo(grpMap) : grpMap.removeLayer(m));
      (layers.spikeMarkers || []).forEach(m => visible ? m.addTo(grpMap) : grpMap.removeLayer(m));
    }
    // Also hide/show that tracker's playback marker+trail if a session is
    // currently active, so unchecking a tracker mid-replay actually hides it.
    const pm = grpPlaybackMarkers[rawId];
    if (pm) {
      if (visible) { pm.marker.addTo(grpMap); pm.trail.addTo(grpMap); pm.trailCasing.addTo(grpMap); }
      else { grpMap.removeLayer(pm.marker); grpMap.removeLayer(pm.trail); grpMap.removeLayer(pm.trailCasing); }
    }
  }

  let grpRawTrackerPoints = {}; // rawId -> every point as loaded from Firestore, BEFORE the accuracy filter -- kept so moving the slider can re-filter instantly without re-querying
  let grpTrackerArrival = {}; // rawId -> ms when this bird got within 200m of its own loft (null = hasn't, or withdrew first)
  let grpTrackerCutoffReason = {}; // rawId -> 'home' | 'withdrawn' | null -- why (if at all) this tracker's points were trimmed below, read by redrawTracker to mark the cutoff point differently from a still-live one

  const GRP_HOME_ARRIVAL_METERS = 200;

  // Looks up which participant (org) a tracker belongs to, by checking
  // whose trackerIds list it's in -- the only way to know "who owns this
  // bird" on a shared flight without reading the (locked) organizations
  // collection directly.
  function findOwningParticipant(g, rawId) {
    if (!g.participants) return null;
    for (const slug of Object.keys(g.participants)) {
      const p = g.participants[slug];
      if ((p.trackerIds || []).includes(rawId)) return p;
    }
    return null;
  }

  // Trims an already time-sorted points array at whichever comes first:
  // the owner's home-loft arrival (within 200m), or the owner withdrawing
  // from this flight -- "each bird stops sharing by itself once it gets
  // home" / "withdrawing stops new positions being shared from this moment
  // on", both from the Join Flight page's own copy. Runs on the RAW points
  // before they're cached in grpRawTrackerPoints, so every later redraw
  // (accuracy slider, spike toggle) stays trimmed too without re-checking.
  function applyGrpShareCutoffs(points, participant) {
    if (!participant || points.length === 0) return { points, cutoffReason: null, arrivalMs: null };
    let homeMs = null;
    if (participant.homeLoft) {
      for (const p of points) {
        const meters = haversineKm(p.lat, p.lng, participant.homeLoft.lat, participant.homeLoft.lng) * 1000;
        if (meters < GRP_HOME_ARRIVAL_METERS) { homeMs = p.t; break; }
      }
    }
    const withdrawnMs = participant.withdrawnAt && participant.withdrawnAt.toMillis ? participant.withdrawnAt.toMillis() : null;
    let cutoffMs = null, cutoffReason = null;
    if (homeMs != null && (withdrawnMs == null || homeMs <= withdrawnMs)) { cutoffMs = homeMs; cutoffReason = 'home'; }
    else if (withdrawnMs != null) { cutoffMs = withdrawnMs; cutoffReason = 'withdrawn'; }
    if (cutoffMs == null) return { points, cutoffReason: null, arrivalMs: null };
    // arrivalMs = the moment this bird got within 200m of its own loft --
    // only when that (not a withdrawal) is what ended its shared track;
    // feeds the flight's Results table.
    return { points: points.filter(p => p.t <= cutoffMs), cutoffReason, arrivalMs: cutoffReason === 'home' ? homeMs : null };
  }

  // Same accuracy filtering as New Track's renderNewTrackContent(): points
  // with no accuracy recorded are kept, only points with a KNOWN accuracy
  // worse than the slider get dropped.
  function grpApplyAccuracyFilter(points) {
    const el = document.getElementById('grp-accuracy-slider');
    const maxAccuracy = el ? parseInt(el.value, 10) : 600;
    return points.filter(p => p.accuracy == null || p.accuracy <= maxAccuracy);
  }

  // Draws (or redraws) one tracker's path/markers on the group map. Moved
  // to module level (out of openGroupDetail) so the accuracy slider's
  // change handler -- attached once, outside any single group's session --
  // can call it too, to re-filter without re-fetching from Firestore.
  function redrawTracker(rawId, accuracyFilteredPoints, color, label) {
    const old = grpTrackerLayers[rawId];
    if (old) {
      [old.casing, old.line, old.decorator].forEach(l => l && grpMap.removeLayer(l));
      old.markers.forEach(m => grpMap.removeLayer(m));
      (old.spikeMarkers || []).forEach(m => grpMap.removeLayer(m));
    }
    // Same outlier/spike step as New Track's renderNewTrackContent() --
    // drops any point implying an unrealistic speed jump from the last KEPT
    // point, so one bad GPS fix doesn't wreck this tracker's distance/speed
    // stats or draw a spike across the combined map. Dropped points are
    // still available via the "Show filtered points" toggle below.
    const points = filterOutliers(accuracyFilteredPoints);
    const keptTimestamps = new Set(points.map(p => p.t));
    const spikePoints = accuracyFilteredPoints.filter(p => !keptTimestamps.has(p.t));
    grpTrackerData[rawId] = { points, spikePoints, color, label };
    grpRenderTrackerTable();
    grpUpdateSpikeToggleUI();
    document.getElementById('grp-play-btn').disabled =
      Object.values(grpTrackerData).reduce((sum, t) => sum + t.points.length, 0) < 2;
    document.getElementById('grp-play-seek').disabled = document.getElementById('grp-play-btn').disabled;
    if (points.length === 0) { grpTrackerLayers[rawId] = { casing: null, line: null, decorator: null, markers: [], spikeMarkers: [] }; return; }
    const visible = grpVisibility[rawId] !== false;
    const latlngs = points.map(p => [p.lat, p.lng]);
    // Same white-cased-colored-line + direction-arrow treatment as New
    // Track's static path, just keeping each tracker's own color instead
    // of the single-tracker grey, so multiple trackers stay distinguishable.
    const casing = L.polyline(latlngs, { color: '#ffffff', weight: 6, opacity: 0.9 });
    const line = L.polyline(latlngs, { color: color.hex, weight: 3, opacity: 0.9 });
    let decorator = null;
    if (visible) { casing.addTo(grpMap); line.addTo(grpMap); }
    if (window.L.polylineDecorator) {
      decorator = L.polylineDecorator(line, {
        patterns: [{
          offset: 20, repeat: 60,
          symbol: L.Symbol.arrowHead({ pixelSize: 9, pathOptions: { color: '#ffffff', weight: 1, fillColor: color.hex, fillOpacity: 0.95 } })
        }]
      });
      if (visible) decorator.addTo(grpMap);
    }
    // Same icon-line popup format as New Track's points: date/time,
    // battery, speed since previous fix, location (looked up on demand
    // when the popup is actually opened), accuracy -- prefixed with
    // which tracker it belongs to, since a group mixes several.
    const popupLine = (icon, html) => `<div class="nt-popup-line"><span class="nt-popup-line-icon">${icon}</span><span>${html}</span></div>`;
    // Set (see applyGrpShareCutoffs in openGroupDetail) when this tracker's
    // points were trimmed at home-arrival or withdrawal -- the final point
    // then gets a static "stopped here" marker instead of the pulsing
    // "currently reporting" one, so it doesn't read as still-live.
    const cutoffReason = grpTrackerCutoffReason[rawId];
    const markers = points.map((p, idx) => {
      const isLast = idx === points.length - 1;
      let marker;
      if (isLast && cutoffReason) {
        marker = L.marker([p.lat, p.lng], {
          icon: L.divIcon({ className: '', html: grpCutoffIconHtml(color.hex, cutoffReason), iconSize: [30, 30], iconAnchor: [15, 15] })
        });
      } else if (isLast) {
        marker = L.marker([p.lat, p.lng], {
          icon: L.divIcon({ className: '', html: grpPulseIconHtml(color.hex), iconSize: [44, 44], iconAnchor: [22, 22] })
        });
      } else {
        marker = L.circleMarker([p.lat, p.lng], {
          radius: 4, color: color.hex, fillColor: color.hex, fillOpacity: 1, weight: 1
        });
      }
      const cutoffSuffix = (isLast && cutoffReason) ? (cutoffReason === 'home' ? ' · Home' : ' · Withdrawn') : '';
      // "Display tracker names" only labels the CURRENT position (isLast) --
      // permanently, so it reads as an always-on name tag rather than the
      // usual hover-for-details tooltip every other point still uses.
      if (isLast && grpShowTrackerNames) {
        marker.bindTooltip(label, { permanent: true, direction: 'top', offset: [0, -20], className: 'nt-point-tooltip' });
      } else {
        marker.bindTooltip(`${label} · ${fmtTime(p.t)}${cutoffSuffix}`, { className: 'nt-point-tooltip' });
      }

      let speedText = null;
      if (idx > 0) {
        const prev = points[idx - 1];
        const meters = haversineKm(prev.lat, prev.lng, p.lat, p.lng) * 1000;
        const minutes = (p.t - prev.t) / 60000;
        if (minutes > 0) speedText = Math.round(meters / minutes) + ' m/min';
      }
      const accuracyText = p.accuracy != null ? '±' + Number(p.accuracy).toFixed(2) + 'm' : '—';
      const cutoffNote = (isLast && cutoffReason)
        ? popupLine(cutoffReason === 'home' ? '🏠' : '⏸', cutoffReason === 'home' ? '<b>Home</b> — nothing reported after this point is shared' : '<b>Withdrawn</b> — this fancier stopped sharing after this point')
        : '';
      marker.bindPopup(`
        ${popupLine('🐦', `<b>${label}</b>`)}
        ${popupLine('🕒', `<b>${fmtTime(p.t)}</b>`)}
        ${cutoffNote}
        ${popupLine('🔋', `Battery: ${p.battery != null ? p.battery + '%' : '—'}`)}
        ${speedText ? popupLine('💨', speedText + ' since the previous fix') : ''}
        ${popupLine('📍', `<span class="nt-popup-address" data-lat="${p.lat}" data-lng="${p.lng}">Looking up location…</span>`)}
        ${popupLine('🎯', `Accuracy: ${accuracyText}`)}
      `);
      marker.on('popupopen', async () => {
        const addr = await reverseGeocode(p.lat, p.lng);
        const el = marker.getPopup().getElement();
        const span = el && el.querySelector('.nt-popup-address');
        if (span) span.textContent = addr;
      });

      if (visible) marker.addTo(grpMap);
      return marker;
    });

    // Filtered/spike points shown only when the "Show filtered points"
    // toggle is on -- same dashed-orange treatment as New Track, never
    // part of the polyline, distance total, or fitBounds. Tied to this
    // tracker's own visibility checkbox too, so hiding a tracker hides its
    // spikes along with everything else.
    const showSpikes = document.getElementById('grp-show-spikes').checked;
    const spikeMarkers = showSpikes ? spikePoints.map(p => {
      const marker = L.circleMarker([p.lat, p.lng], {
        radius: 6, color: '#e65100', fillColor: '#f57c00', fillOpacity: 0.35, weight: 2, dashArray: '4,3'
      });
      marker.bindPopup(`<b>${label}</b><br>${fmtTime(p.t)}<br><i>Filtered out — implied an unrealistic jump</i><br>Accuracy: ${p.accuracy != null ? '±' + p.accuracy + 'm' : '—'}`);
      marker.on('mouseover', () => marker.openPopup());
      marker.on('mouseout', () => marker.closePopup());
      if (visible) marker.addTo(grpMap);
      return marker;
    }) : [];
    grpTrackerLayers[rawId] = { casing, line, decorator, markers, spikeMarkers };
  }

  // Total spike count across every tracker in the group, and whether the
  // toggle is even worth showing at all (hidden entirely when nothing was
  // ever filtered out, same as New Track's toggle).
  function grpUpdateSpikeToggleUI() {
    const total = Object.values(grpTrackerData).reduce((sum, t) => sum + (t.spikePoints ? t.spikePoints.length : 0), 0);
    document.getElementById('grp-spike-toggle-wrap').style.display = total > 0 ? 'inline-block' : 'none';
    document.getElementById('grp-spike-count').textContent = total;
  }

  // Shared by the accuracy slider and the "Show filtered points" toggle --
  // both just need every tracker re-filtered and redrawn from the same raw
  // Firestore data already sitting in grpRawTrackerPoints, no re-query.
  function grpRedrawAllTrackers() {
    Object.keys(grpRawTrackerPoints).forEach(rawId => {
      const t = grpTrackerData[rawId];
      if (!t) return;
      redrawTracker(rawId, grpApplyAccuracyFilter(grpRawTrackerPoints[rawId]), t.color, t.label);
    });
  }

  document.getElementById('grp-accuracy-slider').oninput = (e) => {
    document.getElementById('grp-accuracy-label').textContent = '≤ ' + e.target.value + 'm';
    grpRedrawAllTrackers();
  };

  document.getElementById('grp-show-spikes').onchange = () => grpRedrawAllTrackers();

  async function openGroupDetail(groupId) {
    // A joined (not self-organised) flight lives in joinedFlights, not the
    // owned-groups-only trackerGroups cache -- fall back to it so "View" on
    // a joined flight's card works the same as on one you organised.
    const g = trackerGroups[groupId] || joinedFlights[groupId];
    if (!g) return;
    grpDetachDetailListeners(); // in case a different group's detail was already open
    grpStopPlayback(true);
    document.getElementById('grp-list-view').style.display = 'none';
    document.getElementById('grp-detail-view').style.display = 'block';
    document.getElementById('grp-detail-name').textContent = g.name;

    // "Club flight" badge -- shown for any flight that has (or ever had)
    // real sharing on it, same signal used everywhere else (the card, Join
    // Flight page). "My birds on this flight" only for someone who's
    // actually a participant here (organiser or joined) -- jumps to the
    // same Join Flight page used to join in the first place.
    const isSharedFlight = !!(g.clubShareEnabled || (g.participants && Object.keys(g.participants).length > 1));
    document.getElementById('grp-detail-club-badge').style.display = isSharedFlight ? '' : 'none';
    const mybirdsBtn = document.getElementById('grp-detail-mybirds-btn');
    const iAmParticipant = !!(currentOrg && g.participants && g.participants[currentOrg.slug]);
    mybirdsBtn.style.display = iAmParticipant ? '' : 'none';
    mybirdsBtn.onclick = () => loadJoinFlightView(groupId);

    await initGroupMap();
    setTimeout(() => grpMap && grpMap.invalidateSize(), 50);

    const loadingEl = document.getElementById('grp-map-loading');
    loadingEl.style.display = 'flex';
    grpMap.eachLayer(layer => { if (layer instanceof L.TileLayer) return; grpMap.removeLayer(layer); });
    grpTrackerLayers = {};
    grpTrackerData = {};
    grpVisibility = {};
    grpRawTrackerPoints = {};
    grpTrackerCutoffReason = {};
    grpTrackerArrival = {};
    grpDetailReleaseMarker = null;

    // Release point (if one was set on the group, see the New/Edit Group
    // modal's picker) -- a flag marks it as the START of the flight,
    // distinct from any tracker's own colored dot and from the loft pins
    // below (which mark the opposite end -- home).
    if (g.releasePoint) {
      // Straight-line distance from the release site to THIS viewer's own
      // loft, matching the competitor's popup -- only shown if this org has
      // actually set one (Settings -> Home Loft Location); otherwise there's
      // nothing to measure against.
      const myLoft = currentOrg && currentOrg.homeLoft;
      const distanceLine = myLoft
        ? `<div style="margin-top:6px;">${haversineKm(g.releasePoint.lat, g.releasePoint.lng, myLoft.lat, myLoft.lng).toFixed(1)} km to your loft in a straight line</div>`
        : '';
      L.marker([g.releasePoint.lat, g.releasePoint.lng], {
        icon: L.divIcon({ className: '', html: '<i class="fa-solid fa-flag" style="color:#c0392b;font-size:22px;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.4));"></i>', iconSize: [22, 22], iconAnchor: [3, 20] })
      }).addTo(grpMap).bindPopup(`
        <div style="min-width:170px;">
          <b>Release point</b><br>
          ${g.releasePoint.placeName || '—'}<br>
          <span style="color:#8a97a3;font-size:12px;">Set for this flight</span>
          ${distanceLine}
        </div>
      `);
    }

    grpCurrentGroupData = g; // lets a layers-control toggle redraw without re-fetching
    grpRenderLoftMarkers(g);
    grpRenderExtraLayers();

    const beginMs = g.startTime && g.startTime.toMillis ? g.startTime.toMillis() : 0;
    // No end time = still flying -- use a LIVE listener per tracker so the
    // map keeps updating on its own as new points come in, instead of only
    // showing whatever was true the moment this was opened. A group with a
    // real end time is a completed flight, so that one stays a one-time
    // fetch (nothing new is ever coming for it).
    const isOngoing = !g.endTime;
    const endMs = g.endTime && g.endTime.toMillis ? g.endTime.toMillis() : Date.now();
    grpPlaybackStartMs = beginMs;
    grpPlaybackEndMs = endMs;

    document.getElementById('grp-detail-daterange').textContent =
      fmtDateShort(beginMs) + ' → ' + (isOngoing ? 'now (still in progress)' : fmtDateShort(endMs));

    // Condensed "who's flying" line -- names (Organiser tagged) plus a
    // "N birds from M lofts" summary, matching the competitor's own header.
    // Only for a shared flight; an unshared group has nothing to condense.
    const participantsLineEl = document.getElementById('grp-detail-participants-line');
    // The organiser badge is derived from slug === g.orgId, NOT the stored
    // p.isOrganiser flag -- that flag gets stamped by whoever clicks Save
    // on the Edit form, and an admin editing a group it doesn't own used to
    // overwrite it incorrectly (fixed in the save handler too, but old data
    // saved before that fix can still have it wrong on either side).
    // g.orgId is the one field that's never ambiguous about who really
    // owns this group. Organiser sorted first, matching the competitor.
    const contributingParticipants = Object.entries(g.participants || {})
      // The organiser always shows, even with 0 trackers of their own --
      // not possible today (the Edit form requires at least 1), but kept
      // future-proof for whenever an organiser can run a flight without
      // entering their own birds. Everyone else still needs to have
      // actually entered a bird to be worth listing.
      .filter(([slug, p]) => (p.trackerIds || []).length > 0 || slug === g.orgId)
      .map(([slug, p]) => ({ ...p, isOrganiser: slug === g.orgId, isMe: !!(currentOrg && slug === currentOrg.slug) }))
      .sort((a, b) => (b.isOrganiser ? 1 : 0) - (a.isOrganiser ? 1 : 0));
    if (contributingParticipants.length > 0) {
      // Organiser gets their own line (with the "N birds from M lofts"
      // summary riding on it), plus a second combined "Participant(s)"
      // line listing EVERYONE, organiser included -- the organiser is
      // still a participant too, just also the one who set the flight up.
      const birdCount = (g.trackerIds || []).length;
      const summary = `${birdCount} bird${birdCount !== 1 ? 's' : ''} from ${contributingParticipants.length} loft${contributingParticipants.length !== 1 ? 's' : ''}`;
      const organiser = contributingParticipants.find(p => p.isOrganiser);
      const namesList = contributingParticipants.map(p => `${p.name || 'Unknown'}${p.isMe ? ' (you)' : ''}`).join(', ');
      participantsLineEl.innerHTML = `
        ${organiser ? `
          <div><i class="fa-solid fa-user" aria-hidden="true"></i>
            <b>Organiser</b> ${organiser.name || 'Unknown'}${organiser.isMe ? ' (you)' : ''} &nbsp;—&nbsp; ${summary}
          </div>
        ` : ''}
        <div><i class="fa-solid fa-user-group" aria-hidden="true"></i>
          <b>Participant${contributingParticipants.length !== 1 ? 's' : ''}</b> ${namesList}
        </div>
      `;
      participantsLineEl.style.display = 'block';
    } else {
      participantsLineEl.style.display = 'none';
    }

    // Reuses the same "dummy" 2-min-poll ticker already running for the
    // Trackers tab cards (see fakeCheckedLabel/setInterval above) -- keyed
    // by the group's own id so it ticks independently of any single
    // tracker's real rawId, purely cosmetic either way.
    const updatedEl = document.getElementById('grp-detail-updated');
    updatedEl.dataset.rawid = 'group-' + groupId;
    updatedEl.textContent = fakeCheckedLabel('group-' + groupId);

    document.getElementById('grp-play-btn').disabled = true;
    document.getElementById('grp-play-seek').disabled = true;
    const trackerIds = g.trackerIds || [];

    // Legend row (colored dot + name per tracker) doesn't need to wait for
    // data -- populate it immediately from the group's own tracker list.
    const legendEl = document.getElementById('grp-legend');
    legendEl.innerHTML = trackerIds.map((rawId, i) => {
      const color = MARKER_COLOR_PALETTE[i % MARKER_COLOR_PALETTE.length];
      const regData = registry[rawId] || {};
      const pigeon = matchedPigeon(rawId);
      const label = pigeon ? pigeon.name : (regData.nickname || rawId);
      return `<span class="nt-legend-dot" style="background:${color.hex};"></span> ${label}`;
    }).join(' &nbsp; ');

    let pendingCount = trackerIds.length;
    trackerIds.forEach((rawId, i) => {
      const color = MARKER_COLOR_PALETTE[i % MARKER_COLOR_PALETTE.length];
      const regData = registry[rawId] || {};
      const pigeon = matchedPigeon(rawId);
      const label = pigeon ? pigeon.name : (regData.nickname || rawId);

      let query = db.collection('detections').where('rawCharacteristicId', '==', rawId)
        .where('timestamp', '>=', firebase.firestore.Timestamp.fromMillis(beginMs));
      if (!isOngoing) {
        query = query.where('timestamp', '<=', firebase.firestore.Timestamp.fromMillis(endMs));
      }

      const handleSnapshot = (snapshot) => {
        const points = [];
        snapshot.forEach(doc => {
          const d = doc.data();
          if (typeof d.latitude !== 'number' || typeof d.longitude !== 'number') return;
          const ts = d.timestamp && d.timestamp.toMillis ? d.timestamp.toMillis() : 0;
          points.push({
            lat: d.latitude, lng: d.longitude, t: ts,
            battery: typeof d.battery === 'number' ? d.battery : null,
            accuracy: typeof d.accuracy === 'number' ? d.accuracy : null
          });
        });
        points.sort((a, b) => a.t - b.t);
        const owner = findOwningParticipant(g, rawId);
        const cutoff = applyGrpShareCutoffs(points, owner);
        grpRawTrackerPoints[rawId] = cutoff.points;
        grpTrackerCutoffReason[rawId] = cutoff.cutoffReason;
        grpTrackerArrival[rawId] = cutoff.arrivalMs;
        redrawTracker(rawId, grpApplyAccuracyFilter(cutoff.points), color, label);
        // Only fit the view once, after every tracker's FIRST batch has
        // loaded -- later live updates (ongoing groups) redraw in place
        // without yanking the view around on every new point.
        if (pendingCount > 0) {
          pendingCount--;
          if (pendingCount === 0) {
            loadingEl.style.display = 'none';
            // Includes the release point -- otherwise, if it's far from
            // wherever the birds' GPS points actually are (easily true for
            // a flight that's already covered real distance), fitBounds
            // would zoom to just the tracker data and leave the release
            // marker sitting off-screen, effectively invisible.
            // Deliberately tracker points ONLY, not the release point -- a
            // release site can be far from where the birds actually are,
            // which zoomed the default view out further than useful. The
            // "Go to release point" button (below) is how to reach it
            // instead, on demand rather than by default.
            const allLatLngs = Object.values(grpTrackerData).flatMap(t => t.points).map(p => [p.lat, p.lng]);
            if (allLatLngs.length > 0) grpMap.fitBounds(L.latLngBounds(allLatLngs), { padding: [30, 30], maxZoom: 15 });
          }
        }
      };

      if (isOngoing) {
        grpDetailUnsubscribers.push(query.onSnapshot(handleSnapshot));
      } else {
        query.get().then(handleSnapshot);
      }
    });
  }

  document.getElementById('grp-detail-back-btn').onclick = () => {
    grpDetachDetailListeners();
    grpStopPlayback(true);
    document.getElementById('grp-detail-view').style.display = 'none';
    document.getElementById('grp-list-view').style.display = 'block';
  };

